import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react'
import * as Sentry from '@sentry/react'
import {useAsync} from '../utils/use-async'
import SkeletonLoader from '../components/skeleton-loader'
import {useLocation, useNavigate} from 'react-router-dom'
import {useAuthClient} from '../utils/use-auth-client'
import {UpgradePackageSelection} from './upgrade-package-selection'
import {Catalog} from './catalog'
import {
  MainContainer,
  WizardContainer,
  StepsContainer,
} from '../components/ui/new-order'
import {UpgradeAddonsSelection} from './upgrade-addons-selection'
import {OnboardingProgram} from './onboarding-program'
import PaymentDetails from './payment-details'
import {ADD_ON} from '../utils/addons-data'
import {toast} from 'react-toastify'
import {
  COUNTRIES,
  CURRENCYS,
  CC_CURRENCIES,
  CC_PLANS,
  MC_PLANS,
  BC_PLANS,
  GROWTH_PACKAGES,
  SOCIAL_ADS_BNI_PLAN_CODES,
  KEAP_FAMILY_CODES,
  KEAP_FAMILY_ANNUAL_CODES,
  isWebsite4DesignFeePlanCode,
  MC_2_0_PLANS,
} from '../utils/constants'
import {useUserRoles} from '../utils/use-user-roles'
import {
  BALANCE_CHECK_COUNTRIES,
  hasPastDueInvoice,
} from '../utils/invoice-balance-check'
import {TSSConfirmation} from './tss-confirmation'
import {CountrySelection} from './country-selection'
import {getAuthByRole} from '../utils/get-screen-auth-by-role'
import {useTrackLR} from '../utils/use-track-lr'
import {ADDONS_TYPES} from '../utils/constants'
import {useUserFeatures} from '../context/user-features-context'
import useAutoCoupons from '../utils/use-auto-coupons'
import {getUpgradeIncentivePlans} from '../utils/incentives'
import useDebounce from '../utils/useDebounce'
import {InvoiceInformation} from '../components/account-information-components/invoice-information'
import {NewOrderLayout} from '../components/new-order-layout.js'
import {applyProductRulesBeforeLoading} from '../features/CatalogGrid/utils/productPreloadRules'
import {Modal} from '@thryvlabs/maverick'
import {
  hasBlockingPastDueInvoice,
  SHOW_INVOICE_INFO_FOR_PAST_DUE,
} from '../utils/past-due-invoice.js'
import {selectUserData} from '../app-store/selector.js'
import {useSelector, useDispatch, useStore} from 'react-redux'
import {shallowEqual} from 'react-redux'
import {
  selectMergedCoupons,
  selectManualCoupons,
  selectAutoCoupons,
  selectDefaultCoupons,
  selectUpgradeCoupons,
  selectUpgradePaymentPreview,
  setMergedCoupons,
  setDefaultCoupons as setDefaultCouponsAction,
  setUpgradeCoupons as setUpgradeCouponsAction,
  setUpgradePaymentPreview as setUpgradePaymentPreviewAction,
  resetAllCouponState,
} from '../app-store/coupon'

const TOTAL_STEPS = 5

const KCP_AU_QTY_MAPPING = [
  {amount: 418, qty: 380},
  {amount: 704, qty: 640},
  {amount: 1001, qty: 910},
  {amount: 2002, qty: 1820},
]

const isObjectEmpty = obj => Object.keys(obj).length === 0

const getPlanCode = (currentPlan, selectedPlan, billingPreference) => {
  if (selectedPlan === 'marketingCenter' || selectedPlan === 'thryvLeads') {
    return currentPlan.planCode
  } else if (selectedPlan === 'unlimitedenterprise') {
    return `unlimited-ent-4-dexyp-${
      billingPreference === 'monthly' ? 'm2m' : 'semi'
    }`
  }
  return `${selectedPlan}-4-dexyp-${
    billingPreference === 'monthly' ? 'm2m' : 'semi'
  }`
}

function Container({children, frontendSideNav}) {
  if (frontendSideNav) {
    return <div className="d-flex">{children}</div>
  }
  return children
}

const SubscriptionErrorModal = ({onClose}) => {
  return (
    <Modal
      id="subscription-error-modal"
      openOnLoad
      footer
      action
      variant="default"
      btnActionText="Ok"
      btnAction={() => onClose()}
      hideX
    >
      <div className="text-center">
        <b>
          The Thryv Add-Ons subscription looks inactive.
          <p>
            No worries — just call Client Care, they can sort it out quickly!
          </p>
        </b>
      </div>
    </Modal>
  )
}

const AccountErrorModal = ({onClose}) => {
  return (
    <Modal
      id="account-error-modal"
      openOnLoad
      footer
      action
      variant="default"
      btnActionText="Ok"
      btnAction={() => onClose()}
      hideX
    >
      <div className="text-center">
        <b>
          The Thryv Add-Ons subscription looks inactive.
          <p>
            No worries — just call Client Care, they can sort it out quickly!
          </p>
        </b>
      </div>
    </Modal>
  )
}

function UpgradeOrder({selectedCountry}) {
  const navigate = useNavigate()
  const {state, pathname} = useLocation()
  console.log('order - on init upgrade order', state)

  const {user} = useSelector(selectUserData)

  console.log('user -- upgrade order ==>', user)

  const [order, setOrder] = useState(state?.order || {})
  const [orderData5CU, setOrderData5CU] = useState({})
  const [orderData4CU, setOrderData4CU] = useState({})
  const [order5NUId, setOrder5NUId] = useState(null)
  // const [order4NUId, setOrder4NUId] = useState(null)
  const [order5SelectedLines, setOrder5SelectedLines] = useState({})
  const [order4SelectedLines, setOrder4SelectedLines] = useState({})
  const [order5LinesDiscounts, setOrder5LinesDiscounts] = useState([])
  const [order4LinesDiscounts, setOrder4LinesDiscounts] = useState([])
  const [order5OnboardingApptId, setOrder5OnboardingApptId] = useState(null)
  const [order5CreativeApptId, setOrder5CreativeApptId] = useState(null)
  const [
    order5ThryvSuccessServicesApptId,
    setOrder5ThryvSuccessServicesApptId,
  ] = useState(null)
  const [isWebsiteValid, setIsWebsiteValid] = useState(false)

  const [accountInfo, setAccountInfo] = useState(
    state?.prefetchedAccountInfo
      ? {
          ...state.prefetchedAccountInfo,
          billTo: state.prefetchedAccountInfo.billTo || order?.billTo,
        }
      : order?.v5upgrade && order?.thryvId
      ? {
          thryvID: order.thryvId,
          recurlyCompany: order.locationName || '',
          recurlyEmail: order.customerEmail,
          billTo: order.billTo,
          mobile: order.accountInformation?.mobile || null,
        }
      : {},
  )
  const [accountSubscriptionData, setAccountSubscriptionData] = useState(
    state?.prefetchedAccountSubscriptions || {},
  )
  const [currentStep, setCurrentStep] = useState(1)
  const [allSelectedPurchaseIntent, setAllSelectedPurchaseIntent] = useState({
    firstPurchaseIntent: {},
    secondPurchaseIntent: {},
    thirdPurchaseIntent: {},
    csmExtraInfo: '',
  })
  const [onboardingCallDate, setOnboardingCallDate] = useState(null)
  const [creativeCallDate, setCreativeCallDate] = useState(null)
  const [thryvSuccessServicesCallDate, setThryvSuccessServicesCallDate] =
    useState(null)

  const [processing, setProcessing] = useState(false)
  const [isLoading, setIsLoading] = useState(
    order.v5upgrade || !!state?.prefetchedAccountInfo ? false : true,
  )
  const [kickoffApptId, setKickoffApptId] = useState(null)
  const [creativeApptId, setCreativeApptId] = useState(null)
  const [thryvSuccessServicesApptId, setThryvSuccessServicesApptId] =
    useState(null)

  const [showErrorSubscriptionModal, setShowErrorSubscriptionModal] =
    useState(false)
  const [showErrorAccountModal, setShowErrorAccountModal] = useState(false)
  const couponDispatch = useDispatch()
  const couponStore = useStore()
  const orderCouponCodes = useSelector(selectMergedCoupons, shallowEqual)
  const ccCouponInfo = useSelector(selectManualCoupons, shallowEqual)
  const defaultCouponInfo = useSelector(selectDefaultCoupons, shallowEqual)
  const ccUpgradeCoupons = useSelector(selectUpgradeCoupons)
  const autoCoupons = useSelector(selectAutoCoupons, shallowEqual)
  const setDefaultCouponInfo = useCallback(
    value => couponDispatch(setDefaultCouponsAction(value)),
    [couponDispatch],
  )
  const upgradePaymentPreview = useSelector(selectUpgradePaymentPreview)
  const setOrderCouponCodes = useCallback(
    valueOrUpdater => {
      if (typeof valueOrUpdater === 'function') {
        const current = couponStore.getState().ccCouponState.mergedCoupons
        couponDispatch(setMergedCoupons(valueOrUpdater(current)))
      } else {
        couponDispatch(setMergedCoupons(valueOrUpdater))
      }
    },
    [couponDispatch, couponStore],
  )
  const setCcUpgradeCoupons = useCallback(
    value => couponDispatch(setUpgradeCouponsAction(value)),
    [couponDispatch],
  )
  const setUpgradePaymentPreview = useCallback(
    value => couponDispatch(setUpgradePaymentPreviewAction(value)),
    [couponDispatch],
  )
  const [cartProducts, setCartProducts] = useState([])
  const [previewRequestFinished, setPreviewRequestFinished] = useState(false)
  const [all5PlanPrices, setAll5PlanPrices] = useState(
    state?.prefetchedPlanPrices || null,
  )
  const [isInvoiceBilled, setIsInvoiceBilled] = useState(() => {
    const prefetched =
      state?.prefetchedEmailValidation?.isInvoiceBilled ?? false
    if (!prefetched || !state?.order?.v5upgrade) return prefetched
    const country =
      state?.prefetchedAccountSubscriptions?.accountInfo?.businessAddress
        ?.country
    // For balance-check countries (GEP-7047: AU/NZ/US/CA) v5upgrade, start
    // false and let useEffect set true only if past-due
    return BALANCE_CHECK_COUNTRIES.includes(country) ? false : prefetched
  })
  const [showLegacyContactsModal, setShowLegacyContactsModal] = useState(false)
  const [showPastDueInvoiceModal, setShowPastDueInvoiceModal] = useState(false)
  // {thryvId, email, country} captured at the moment a past-due block fires —
  // needed to render InvoiceInformation, since accountInfo isn't always set
  // yet at that point (we return before setAccountInfo in some branches).
  const [pastDueInvoiceContext, setPastDueInvoiceContext] = useState(null)
  const [isEaidFound, setIsEaidFound] = useState(false)
  const [cohortEaid, setCohortEaid] = useState(false)
  const [allowInvoice, setAllowInvoice] = useState(false)
  const [isCohort, setIsCohort] = useState(false)
  const [showSmsOption, setShwoSmsOption] = useState(false)
  const [keapTrailAppId, setKeapTrailAppId] = useState(null)
  const [keapTextVoiceQty, setKeapTextVoiceQty] = useState(1)
  const [keapTextVoiceQtyAnnual, setKeapTextVoiceQtyAnnual] = useState(1)
  const [keapAddContactsQty, setKeapAddContactsQty] = useState('')
  const [keapAddContactsQtyAnnual, setKeapAddContactsQtyAnnual] = useState('')
  const [keapAddContactsQtyGraphlyAnnual, setKeapAddContactsQtyGraphlyAnnual] =
    useState('')
  const [plusThisPremiumQtyGraphlyAnnual, setPlusThisPremiumQtyGraphlyAnnual] =
    useState('')
  const [keapAddContactsQtyGraphlyM2m, setKeapAddContactsQtyGraphlyM2m] =
    useState('')
  const [plusThisPremiumQtyGraphlyM2m, setPlusThisPremiumQtyGraphlyM2m] =
    useState('')
  console.log('keapAddContactsQtyAnnual :: ', keapAddContactsQtyAnnual)
  console.log(
    'keapAddContactsQtyGraphlyAnnual :: ',
    keapAddContactsQtyGraphlyAnnual,
  )
  const userFeatures = useUserFeatures()
  const {
    TL_MC_Access: isTLMCAccess,
    TL_Tax_Plan_Enabled: isNewTLPlanEnabled = false,
    KeapMax_Catalog_Provisions: hasKeapMaxAccess,
    Keap_Account_Ability,
    ThryvHVAC_Catalog_Provisions: hasKeapHVACAccess,
    // Custom_Data_Service_Access: hasCustomDataServiceAccess,
    Custom_Data_For_You_Access: hasCustomDataFYAccess,
  } = userFeatures
  const isNewTLMCAccessible = isTLMCAccess
  const catalogPlanPrices = useMemo(() => {
    if (!all5PlanPrices) return null
    const rulesFiltered = applyProductRulesBeforeLoading(
      all5PlanPrices,
      userFeatures,
    )
    const existingTLWithoutTax =
      accountSubscriptionData?.childSubscriptions?.some(
        sub => sub.code === 'tl-mc-5-thryv-m2m',
      )
    const codeToRemove = isNewTLPlanEnabled
      ? existingTLWithoutTax
        ? 'tl-tax-5-thryv-m2m'
        : 'tl-mc-5-thryv-m2m'
      : 'tl-tax-5-thryv-m2m'
    const filterList = list =>
      list?.filter(item => item?.code !== codeToRemove) ?? []
    return {
      ...rulesFiltered,
      centers: filterList(rulesFiltered.centers),
      apps: filterList(rulesFiltered.apps),
      addons: filterList(rulesFiltered.addons),
    }
  }, [
    all5PlanPrices,
    isNewTLMCAccessible,
    isNewTLPlanEnabled,
    accountSubscriptionData,
    userFeatures,
  ])
  const [hasKeapAccountAbility, setHasKeapAccountAbility] =
    useState(Keap_Account_Ability)
  const [keapAccount, setKeapAccount] = useState(
    accountInfo?.keapAccount ||
      accountSubscriptionData?.accountInfo?.keapAccount ||
      false,
  )
  const [keapID, setKeapID] = useState(accountInfo?.keapID || '')
  const [isBillingNoticeDisplayed, setIsBillingNoticeDisplayed] =
    useState(false)
  console.log(
    'isBillingNoticeDisplayed from upgrade-order.js is :: ',
    isBillingNoticeDisplayed,
  )
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState('Credit Card')
  const defaultCentersAndApps = useRef()
  const expiredCentersAndApps = useRef()
  const previewToastShown = useRef(false)
  const isCreatingOrder = useRef(false)
  const client = useAuthClient()
  const {run} = useAsync({
    status: 'pending',
  })
  const navFrom = state?.navFrom
  //const STARTER_PACKAGE_ADDON_CODES = ['addon-5-mcstartgr', 'addon-5-mcexpangr']
  const {roles} = useUserRoles()
  const {
    Frontend_Preview_Payment: frontendPreviewPayment,
    Frontend_Side_Nav: frontendSideNav,
    Listings_BC_MC: frontendListingsBcMc,
  } = useUserFeatures()
  const customEvent = {
    key: 'upgrade-order',
    ...(accountInfo.recurlyEmail && {companyEmail: accountInfo.recurlyEmail}),
    ...(accountInfo.recurlyCompany && {
      companyName: accountInfo.recurlyCompany,
    }),
    ...(accountInfo.thryvID && {thryvId: accountInfo.thryvID}),
  }
  useTrackLR(customEvent)
  const isCCPlan =
    accountInfo?.planCode?.includes('-5-') ||
    accountInfo?.planCode?.includes('-6-') ||
    order?.v5upgrade
  let selectedPlanCode
  if (!isCCPlan) {
    if (order.selectedPlan?.name === 'unlimitedenterprise') {
      selectedPlanCode = `unlimited-ent-4-dexyp-${
        order.billingPreference?.value === 'monthly' ? 'm2m' : 'semi'
      }`
    } else {
      selectedPlanCode = `${order.selectedPlan?.name}-4-dexyp-${
        order.billingPreference?.value === 'monthly' ? 'm2m' : 'semi'
      }`
    }
  }
  const isUpgrade = pathname === '/order/upgrade'
  const isNewOrder = pathname === '/order/new'
  console.log('isUpgrade', isUpgrade)
  console.log('isNewOrder', isNewOrder)
  const eligibleTransitionCenters =
    isUpgrade &&
    order.selectedCentersAndApps &&
    getUpgradeIncentivePlans(
      order.selectedCentersAndApps?.centers,
      defaultCentersAndApps.current?.centers,
      upgradePaymentPreview,
    )
  let clientType = null
  if (keapAccount) {
    clientType = 'keap'
  }
  const {resetAutoCoupons} = useAutoCoupons({
    order: order,
    accountInfo: accountInfo,
    selectedPackage: selectedPlanCode,
    selectedCountry: COUNTRIES[accountInfo.billingAddress?.country],
    selectedCentersAndApps: order.selectedCentersAndApps,
    defaultCentersAndApps: defaultCentersAndApps,
    expiredCentersAndApps: expiredCentersAndApps,
    eligibleTransitionCenters,
    selectedAddons: order.selectedAddons,
    upgradeAccountInfo: accountInfo,
    currentStep,
    setOrderCouponCodes,
    isEaidFound,
    isCohort,
    clientType,
  })

  // Clean up coupon state when leaving the order screen
  useEffect(() => {
    return () => couponDispatch(resetAllCouponState())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeDuplicatesFromArray = (arrayObject, comparisionAttribute) => {
    const uniqueArrayElements = new Set()
    return arrayObject.filter(item => {
      const keyValue = item[comparisionAttribute]
      return uniqueArrayElements.has(keyValue)
        ? false
        : (uniqueArrayElements.add(keyValue), true)
    })
  }
  const hasWebsiteAddon = addons => addons?.some(({name}) => name === 'Website')

  const isWebsiteIncluded =
    hasWebsiteAddon(accountInfo?.addOns) ||
    hasWebsiteAddon(accountInfo?.legacyAddOns)
  const isSocialBoostIncluded = accountInfo?.addOns?.some(
    ({name}) => name === ADD_ON.entitlements['Social Boost'].label,
  )
  const isSEOIncluded =
    order?.selectedCentersAndApps?.centers?.some(
      ({name, alreadyIncluded}) => name.includes('SEO') && !alreadyIncluded,
    ) ||
    order?.selectedCentersAndApps?.apps?.some(
      ({name, alreadyIncluded}) => name.includes('SEO') && !alreadyIncluded,
    )

  const isTLLegacyIncluded = accountInfo?.legacyAddOns?.find(addOn =>
    addOn?.addOnCode?.includes('Thryv Leads'),
  )

  const getGmailItems = () => {
    let result = []
    const gmailStandard = accountInfo?.addOns?.find(
      ({name}) => name === 'Gmail Standard',
    )
    const gmailStandardFree = accountInfo?.addOns?.find(
      ({name}) => name === 'Gmail Standard Free',
    )
    const gmailCertified = accountInfo?.addOns?.find(
      ({name}) => name === 'Gmail Certified',
    )
    if (gmailStandard) {
      result.push(gmailStandard)
    }
    if (gmailStandardFree) {
      result.push(gmailStandardFree)
    }
    if (gmailCertified) {
      result.push(gmailCertified)
    }
    return result
  }

  console.log('user roles', roles)
  const isTSS = getAuthByRole(['tss'], roles)
  const isAus = selectedCountry === 'AU'
  const isNZ = selectedCountry === 'NZ'
  const isClientCare = getAuthByRole(['clientcare'], roles)
  const isReseller = getAuthByRole(['reseller'], roles)

  useEffect(() => {
    if (state?.prefetchedPlanPrices) return
    const get5PlanPrices = async () => {
      const url = 'plans/5/v1/list'
      const response = await client(url)
      console.log('response111', response)
      setAll5PlanPrices(response)
    }

    get5PlanPrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // useEffect(() => {
  //   if (kickoffApptId && !roles.includes('PartnerOps')) {
  //     return () => {
  //       client(`plans/onboarding`, {
  //         data: {
  //           type: 'onboarding',
  //           country: accountInfo?.billingAddress?.country,
  //           demoApptId: kickoffApptId,
  //         },
  //         method: 'DELETE',
  //       })
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [kickoffApptId])

  // useEffect(() => {
  //   if (creativeApptId && !roles.includes('PartnerOps')) {
  //     return () => {
  //       client(`plans/creative`, {
  //         data: {
  //           type: 'creative',
  //           country: accountInfo?.billingAddress?.country,
  //           demoApptId: creativeApptId,
  //         },
  //         method: 'DELETE',
  //       })
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [creativeApptId])

  const nextStep = data => {
    console.log('next step - upgrade order', data)
    console.log(
      'next step - upgrade order - accountSubscriptionData',
      accountSubscriptionData,
    )
    if (data?.selectedCentersAndApps?.oneTimes) {
      const isKeapInCart = data.selectedCentersAndApps.centers?.some(c =>
        KEAP_FAMILY_CODES.includes(c.code),
      )
      let updatedOneTimesData = data.selectedCentersAndApps.oneTimes.filter(
        item => {
          if (
            item.code === 'thryvprfee' &&
            !isKeapInCart &&
            (accountInfo?.hasPaidOnboardingFee ||
              accountSubscriptionData?.accountInfo?.hasPaidOnboardingFee)
          ) {
            return false
          }
          if (
            item.code === 'thryvdesg2' &&
            (accountInfo?.hasPaidDesignFee ||
              accountSubscriptionData?.accountInfo?.hasPaidDesignFee)
          ) {
            return false
          }
          return true
        },
      )
      data.selectedCentersAndApps.oneTimes = updatedOneTimesData
    }

    setOrder({...order, ...data})
    let _currentStep = currentStep
    if (currentStep >= TOTAL_STEPS - 1) {
      _currentStep = TOTAL_STEPS
    } else {
      _currentStep = currentStep + 1
    }

    if (isUpgrade && !order?.selectedCentersAndApps && order?.selectedAddons) {
      getLegacyPaymentPreview()
    }
    if (
      (_currentStep === 5 && !isTSS && !isCCPlan) ||
      (_currentStep === 4 && isCCPlan)
    ) {
      accountInfo.email
        ? navigate('/success', {state: {email: accountInfo.email}})
        : navigate('/success')
    }

    setCurrentStep(_currentStep)
  }

  const prevStep = () => {
    console.log('From prevStep function , currentStep is :: ', currentStep)
    console.log(
      'From prevStep function , isBillingNoticeDisplayed is :: ',
      isBillingNoticeDisplayed,
    )
    if (
      isBillingNoticeDisplayed &&
      ((currentStep === 3 && !isCCPlan) || (currentStep === 2 && isCCPlan))
    ) {
      setIsBillingNoticeDisplayed(false)
    }
    let _currentStep = currentStep
    if (currentStep <= 1) {
      _currentStep = 1
      navigate(!isReseller ? '/all-accounts' : '/my-accounts')
    } else {
      _currentStep = currentStep - 1
    }
    setCurrentStep(_currentStep)
  }

  const getListOfAddons = data => {
    let isNewSEOAdded = data.find(item =>
      ['SEO Keyword', 'AEO & SEO', 'AEO & SEO PowerBoost'].includes(
        item?.product.trim(' '),
      ),
    )
    if (isNewSEOAdded) {
      data = data.filter(
        item =>
          !['SEO Money Back Guarantee', 'SEO Non-Guaranteed'].includes(
            item.product,
          ),
      )
    }
    return data
  }

  const isBlockedByPastDueInvoice = useCallback(
    async (thryvId, country, email) => {
      const isBlocked = await hasBlockingPastDueInvoice(
        client,
        thryvId,
        country,
      )
      if (isBlocked) {
        setShowPastDueInvoiceModal(true)
        setPastDueInvoiceContext({thryvId, country, email})
      }
      return isBlocked
    },
    [client],
  )

  const fetchAccountInfo = useCallback(async () => {
    if (order?.v5upgrade) return
    // When the caller pre-fetched before navigating, skip the two round-trips
    // and apply only the derived side-effects (CCAccountInfo, currentStep, etc.)
    if (state?.prefetchedAccountInfo) {
      const response = state.prefetchedAccountInfo
      response.thryvID = response.thryvID || order.thryvId
      if (
        await isBlockedByPastDueInvoice(
          response.thryvID,
          response.billingAddress?.country,
          response.recurlyEmail,
        )
      ) {
        setIsLoading(false)
        return
      }
      if (isCCPlan) {
        setOrder({
          ...order,
          CCAccountInfo: {thryvId: response.thryvID},
          ...(!order.template && {template: response.industry}),
        })
      }
      setOrderData4CU({order, accountInfo: response})
      if (!response?.billingAddress?.country) setCurrentStep(0)
      if (
        state?.prefetchedEmailValidation?.isInvoiceBilled &&
        BALANCE_CHECK_COUNTRIES.includes(response.billingAddress?.country)
      ) {
        try {
          const {openInvoices} = await client(`invoices/${response.thryvID}`)
          const hasPastDueInvoices = hasPastDueInvoice(openInvoices)
          if (!hasPastDueInvoices) setIsInvoiceBilled(false)
          // else: has past-due → isInvoiceBilled stays true (initialized from prefetchedEmailValidation)
        } catch {
          setIsInvoiceBilled(true)
        }
      }
      setIsLoading(false)
      return
    }
    try {
      let url = `subscription/uuid-${order.billingAccountId}`
      const response = await client(url)
      response.thryvID = response.thryvID || order.thryvId
      if (
        await isBlockedByPastDueInvoice(
          response.thryvID,
          response.billingAddress?.country,
          response.recurlyEmail,
        )
      ) {
        setIsLoading(false)
        return
      }
      const emailValidation = await client(
        `orders/validate-email/${response.recurlyEmail}`,
      )
      if (emailValidation.isInvoiceBilled) {
        const country = response.billingAddress?.country
        if (BALANCE_CHECK_COUNTRIES.includes(country)) {
          try {
            const {openInvoices} = await client(`invoices/${response.thryvID}`)
            const hasPastDueInvoices = hasPastDueInvoice(openInvoices)
            if (!hasPastDueInvoices) {
              // invoice billed with no past-due invoices → go straight to catalog
            } else {
              setIsInvoiceBilled(true)
            }
          } catch {
            setIsInvoiceBilled(true)
          }
        } else {
          setIsInvoiceBilled(true)
        }
      }
      if (emailValidation.isLegacyContactsAvailable)
        setShowLegacyContactsModal(true)
      if (isCCPlan) {
        setOrder({
          ...order,
          CCAccountInfo: {thryvId: response.thryvID},
          ...(!order.template && {template: response.industry}),
        })
      }
      setAccountInfo(response)
      setOrderData4CU({
        order: order,
        accountInfo: response,
      })
      console.log('orderData4CU - fetchAccountInfo', {
        order: order,
        accountInfo: response,
      })
      if (!response?.billingAddress?.country) {
        setCurrentStep(0)
      }
      setIsLoading(false)
    } catch (err) {
      if (err.status === 404) {
        toast.error(err.message)
        navigate(-1)
      }
      if (err.status !== 404) throw err
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    order.billingAccountId,
    order.thryvId,
    isCCPlan,
    isBlockedByPastDueInvoice,
  ])

  const sendPaymentLink = async (contactMethod, contactInfo) => {
    const newAddons =
      order?.selectedAddons?.filter(
        ({planCode}) =>
          !accountInfo.addOns.some(({addOnCode}) => addOnCode === planCode) &&
          accountInfo.planCode !== planCode,
      ) || []
    const noNewAddonsAdded = newAddons.length === 0
    const allNewAddonsAreEntitlements = newAddons.every(
      ({type}) => type === ADDONS_TYPES.ENTITLEMENTS,
    )
    const intents = !order.onBoardingProgramData.intents.every(isObjectEmpty)
      ? order.onBoardingProgramData.intents.map(intent => intent.option)
      : []
    const altPlanCodes = [
      ...ADD_ON.add_ons['Marketing Center Plus'].addOnCodes,
      ...ADD_ON.add_ons['Thryv Leads'].addOnCodes,
    ]
    const addonPlanCodes = getListOfAddons(order.selectedAddons)
      .filter(({product}) => product !== 'One Time Design Fee')
      .filter(({planCode}) => planCode)
      .filter(({planCode}) => !planCode.includes('dexyp'))
      .filter(({planCode}) => !planCode.includes('-setupfee'))
      .filter(
        ({type, planCode}) =>
          !(
            type === ADDONS_TYPES['SELECTED_PLAN'] &&
            altPlanCodes.includes(planCode)
          ),
      )
      .map(({planCode, quantity = 1}) => {
        return {
          addonPlanCode: planCode,
          quantity,
        }
      })

    const websiteQty =
      order.selectedAddons.find(
        addon =>
          addon.product === ADD_ON.one_time['5 Extra Website Pages'].label,
      )?.quantity || 0
    const thryvLeadsId = order.selectedAddons.find(addon => addon.thryvLeadsId)
    const thryvLeadBillingDate = order.selectedAddons.find(
      addon => addon.thryvLeadBillingDate,
    )
    const seoProposalId = order.selectedAddons.find(
      addon => addon.seoProposalId,
    )
    const seobase4Addon = order.selectedAddons.find(
      addon => addon.planCode === 'addon-4-seobase-m2m',
    )
    const seoBillingDate = order.selectedAddons.find(
      addon => addon.seoBillingDate,
    )
    const mcAddon = order.selectedAddons.find(
      x => x.product === ADD_ON.add_ons['Marketing Center Plus'].label,
    )
    const webSiteDomain = order.selectedAddons.find(
      addon => addon.sensisSiteDomain,
    )
    const subchannel = order.subchannel
    const leadId = order.leadId
    const selectedPlanCode = getPlanCode(
      {planCode: accountInfo.planCode, planName: accountInfo.planName},
      order.selectedPlan.name,
      order.billingPreference.value,
    )

    let sendLinkVia = contactMethod
    if (isClientCare) {
      sendLinkVia = 'none'
    } else if (isTSS && (allNewAddonsAreEntitlements || noNewAddonsAdded)) {
      sendLinkVia = 'none'
    }
    console.log('AccountInfo =======> upgrade order', accountInfo)
    const data = {
      firstName: accountInfo.recurlyFirst,
      lastName: accountInfo.recurlyLast,
      email: accountInfo.recurlyEmail,
      package: selectedPlanCode,
      company: accountInfo.recurlyCompany,
      postCode: accountInfo.billingAddress.postalCode,
      address: accountInfo.recurlyStreet,
      city: accountInfo.billingAddress.city,
      state: accountInfo.billingAddress.region,
      country: accountInfo.billingAddress.country,
      curr: CURRENCYS[COUNTRIES[accountInfo.billingAddress.country]?.value],
      mobile: accountInfo.recurlyPhone,
      companyPhone: accountInfo.billingAddress.phone,
      software: order.selectedPlan.industryType,
      purchasePlanCode: selectedPlanCode,
      addonPlanCodes,
      intents,
      contact: {
        sendLinkVia,
      },
      isSync: accountInfo?.product?.toLowerCase() === 'thryv' ? false : true,
      planType: order.billingPreference.value === 'monthly' ? 'm2m' : 'semi',
      currentPlanCode: accountInfo.planCode,
      thryvId: accountInfo?.thryvID,
      recurlyAccountId: accountInfo?.recurlyAccountId,
      orderNotes: order?.onBoardingProgramData?.csmExtraInfo,
    }
    if (accountInfo?.industry) {
      data.industryId = accountInfo.industry
    }
    if (thryvLeadsId) {
      data.thryvLeadsId = thryvLeadsId.thryvLeadsId
    }
    if (thryvLeadBillingDate) {
      data.thryvLeadsDate = thryvLeadBillingDate.thryvLeadBillingDate
    }
    if (seoProposalId) {
      data.seoProposalId = seoProposalId.seoProposalId
    }
    if (seoProposalId && seoProposalId.seoDomain) {
      data.seoDomain = seoProposalId.seoDomain
    }
    if (
      seoBillingDate &&
      new Date(seoBillingDate.seoBillingDate) >=
        new Date(new Date().toDateString())
    ) {
      data.seoDate = seoBillingDate.seoBillingDate
    }
    if (mcAddon) {
      data.sensisSiteDomain = mcAddon.sensisSiteDomain
    } else if (webSiteDomain) {
      data.sensisSiteDomain = webSiteDomain.sensisSiteDomain
    }
    if (subchannel) {
      data.subchannel = subchannel
    }
    if (leadId) {
      data.leadId = leadId
    }
    if (websiteQty > 0) {
      data.websiteQty = websiteQty
    }
    if (kickoffApptId) {
      data.onboardingApptId = kickoffApptId
    }
    if (creativeApptId) {
      data.creativeApptId = creativeApptId
    }
    if (thryvSuccessServicesApptId) {
      data.thryvSuccessServicesApptId = thryvSuccessServicesApptId
    }
    if (order?.xpCode) {
      data.xpCode = order.xpCode
    }
    if (order?.differentSalesRep?.value) {
      data.behalf = order.salesRepCode
      if (order.behalfFirst) {
        data.behalfFirst = order.behalfFirst
      }
      if (order.behalfLast) {
        data.behalfLast = order.behalfLast
      }
      if (order.behalfEmail) {
        data.behalfEmail = order.behalfEmail
      }
    }
    if (order?.overrideRep?.value) {
      data.overrideRepId = order.overrideRepCode
      if (order.overrideRepFirst) {
        data.overrideRepFirstName = order.overrideRepFirst
      }
      if (order.overrideRepLast) {
        data.overrideRepLastName = order.overrideRepLast
      }
      if (order.overrideRepEmail) {
        data.overrideRepEmail = order.overrideRepEmail
      }
    }
    if (order.clientId) {
      data.enterpriseId = order.clientId
    }
    if (contactMethod === 'sms') {
      data.contact.mobile = contactInfo
    } else if (contactMethod === 'email') {
      data.contact.email = contactInfo
    }
    const COUPON_CODES = orderCouponCodes.map(coupon => {
      return coupon.code
    })
    const DESIGN_FEE_WAIVE_COUPON = 'urldbdesignfee'
    const hasDesignFeeWaive = autoCoupons?.some(
      coupon => coupon.code === DESIGN_FEE_WAIVE_COUPON,
    )
    if (hasDesignFeeWaive && !COUPON_CODES.includes(DESIGN_FEE_WAIVE_COUPON)) {
      COUPON_CODES.push(DESIGN_FEE_WAIVE_COUPON)
    }
    if (COUPON_CODES.length > 0) {
      data.couponCodes = COUPON_CODES
    }
    if ('sensisSiteDomain' in data && !data.sensisSiteDomain) {
      delete data.sensisSiteDomain
    }
    if (
      selectedPaymentMethod &&
      ['AU', 'NZ'].includes(accountInfo?.billingAddress?.country)
    ) {
      data.isInvoiceBilled = selectedPaymentMethod === 'Invoice'
    }
    if (cohortEaid) {
      data.cohortEaid = cohortEaid
    }
    if (allowInvoice && selectedPaymentMethod !== 'Invoice') {
      data.updateToAutopay = true
    }
    if (seobase4Addon) {
      data.seoDomain = seobase4Addon.domain || ''
      data.seoProposalId = seobase4Addon.proposalId || null
    }

    console.log('send link data =======> upgrade order', data)
    data.appUrl = window.location.origin
    if (order5NUId) {
      data.orderHeaderId = order5NUId
    }

    try {
      const result = await run(
        client('orders/process/upgrade/send-link', {data}),
      )
      if (result?.randomId) {
        toast.success('Link sent successfully')
        updateOrder5Status('send-link', 'Submitted', result)
        return result
      } else {
        toast.error('Failed to send the link')
        handleSendLinkFailEmail(contactMethod)
      }
    } catch (error) {
      handleSendLinkFailEmail(contactMethod)
      setProcessing(false)
      if (error.status !== 400) {
        throw error
      }
      if (error.errors) {
        error.errors.forEach(({message}) => toast.error(message))
      } else {
        toast.error(error.message)
      }
    }
  }

  const handleSendLinkFailEmail = async contactMethod => {
    try {
      const data = {
        orderKeyer: user.network_id,
        contactMethod: contactMethod,
        onBehalf: accountInfo?.onBehalf || null,
      }
      const result = await run(client('orders/process/send-link-fail', {data}))
      console.log('log: sendlink fail email response --->', result)
    } catch (error) {
      toast.error('Error in sending failed Send Link email')
    }
  }

  const findWebsiteEntitlement = centers =>
    centers
      .find(({entitlements}) =>
        entitlements?.some(
          ({name}) => name === 'Professionally Designed Website',
        ),
      )
      ?.entitlements?.find(
        ({name}) => name === 'Professionally Designed Website',
      )

  const findWebsiteApp = ({name}) => name === 'Professionally Designed Website'

  const formatCenters = (centers, callbackFn) =>
    centers
      .filter(callbackFn)
      .map(
        ({code, quantity = 1, entitlements, startDate, tempLineActionCode}) => {
          return {
            code,
            quantity,
            ...(startDate && new Date(startDate) >= new Date() && {startDate}),
            addons: entitlements?.map(({code, quantity}) => {
              // const entitlementQuantity =
              //   code === 'ent-keap-included-contacts'
              //     ? 1500
              //     : code === 'ent-keap-included-user'
              //     ? 2
              //     : 1
              return {
                code,
                quantity: quantity ?? 1,
              }
            }),
          }
        },
      )

  const formatApps = (apps, callbackFn) =>
    apps.filter(callbackFn).map(({code, quantity = 1, tempLineActionCode}) => {
      return {
        code,
        quantity,
        addons: [],
      }
    })

  const sendCCPaymentLink = async (contactMethod, contactInfo) => {
    let {centers, apps, oneTimes} = order.selectedCentersAndApps
    const thryvLeadsId = (
      centers.find(({name}) => name === 'Thryv Leads') ||
      apps.find(({name}) => name === 'Thryv Leads')
    )?.thryvLeadsId
    const seoProposal = centers.find(
      ({name}) => name.includes('SEO') && name !== 'AEO & SEO',
    )
    const alphaSeo =
      centers.find(({name}) => name === 'AEO & SEO') ||
      apps.find(({name}) => name === 'AEO & SEO')
    const sensisDomain = (
      findWebsiteEntitlement(centers) || apps.find(findWebsiteApp)
    )?.sensisSiteDomain
    const mcDomain = centers.find(
      ({name, code}) =>
        name.toLowerCase().includes('marketing') || MC_2_0_PLANS.includes(code),
    )?.mcDomain
    const tlDomain = (
      centers.find(({code}) =>
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          code,
        ),
      ) ||
      apps.find(({code}) =>
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          code,
        ),
      )
    )?.tlDomain
    const keapDomain = centers.find(
      ({code}) =>
        KEAP_FAMILY_CODES.includes(code) ||
        KEAP_FAMILY_ANNUAL_CODES.includes(code),
    )?.keapDomain
    const parentSubscriptions = formatCenters(centers, ({code}) =>
      code.startsWith('cc_'),
    )
    const marketplace = apps.find(({code}) => code === 'appmarket-5-thryv-m2m')
    const isBCorMCInCart = centers.some(
      ({code}) => MC_PLANS.includes(code) || BC_PLANS.includes(code),
    )
    let childSubscriptions = []
    for (let center of centers) {
      if (center?.code.startsWith('cc_')) {
        continue
      } else {
        const formattedCenter = formatCenters(centers, ({name}) =>
          name.includes(center?.name),
        )
        childSubscriptions.push(formattedCenter[0])
      }
    }

    apps = apps.map(item => {
      let addOnItem = item
      if (addOnItem.code === 'appmarket-5-thryv-m2m' && alphaSeo) {
        addOnItem.addons = (addOnItem.addons || []).filter(
          ({code}) =>
            ![
              'seombg-5-thryv-m2m',
              'seo-5-thryv-m2m',
              'addon-5-leadslift-m2m',
              'addon-5-leadslift-bni-m2m',
            ].includes(code),
        )
      }
      return addOnItem
    })
    apps = apps.filter(
      ({code}) => !['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(code),
    )
    // GEP-6803: BNI is read-only display (provisioned in Salesforce, not
    // managed via OOE). It surfaces in the catalog cart for visibility but
    // must NEVER be persisted into the order — neither as a standalone
    // childSubscription nor as an addon under appmarket. Strip here once
    // so every downstream push (line 1071 marketplace addons, line 1149+
    // childSubscriptions.push paths, etc.) sees a BNI-free apps list.
    apps = apps.filter(({code}) => !SOCIAL_ADS_BNI_PLAN_CODES.includes(code))
    if (isNewTLMCAccessible || isCCPlan) {
      let isTLLeadExist =
        centers.find(item =>
          [
            'tl-mc-5-thryv-m2m',
            'tl-tax-5-thryv-m2m',
            'tl-5-thryv-m2m',
          ].includes(item.code),
        ) ||
        apps.find(item =>
          [
            'tl-mc-5-thryv-m2m',
            'tl-tax-5-thryv-m2m',
            'tl-5-thryv-m2m',
          ].includes(item.code),
        )
      if (isTLLeadExist) {
        apps = apps.filter(({name}) => {
          return !name.includes('Growth Package')
        })
      }
    }
    const isBCUnlimitedInOrder = childSubscriptions.some(
      ({code}) => code === 'bc_unlm-5-thryv-m2m',
    )

    const thryvMaxAddonsMonthlyApp = apps.find(
      ({code}) =>
        code === 'addon-5-keap-addons' ||
        code === 'addon-5-keap-addons-legacy-m2m',
    )
    const thryvMaxAddonproducts = (all5PlanPrices?.apps || [])
      .filter(
        app =>
          (app.parentProduct === 'addon-5-keap-addons' ||
            app.parentProduct === 'addon-5-keap-addons-legacy-m2m') &&
          app.code != 'addon-5-keap-addons' &&
          app.cpde != 'addon-5-keap-addons-legacy-m2m',
      )
      .map(item => item.code)
    if (thryvMaxAddonsMonthlyApp) {
      //Thryv Max Add Ons subscription
      const hasExplicitKeapAddContactsQty =
        keapAddContactsQty !== '' &&
        keapAddContactsQty !== null &&
        keapAddContactsQty !== undefined

      childSubscriptions.push({
        code: thryvMaxAddonsMonthlyApp.code,
        quantity: 1,
        addons: apps
          .filter(item => thryvMaxAddonproducts.includes(item.code))
          .map(({code, quantity}) => {
            if (code === 'addon-5-keap-textvoice-m2m') {
              quantity = keapTextVoiceQty
            }
            if (
              code === 'addon-5-keap-addcontacts-m2m' &&
              hasExplicitKeapAddContactsQty
            ) {
              quantity = keapAddContactsQty
            }
            if (code === 'addon-5-keap-legacy-graphly-contacts-m2m') {
              quantity = keapAddContactsQtyGraphlyM2m
            }
            if (code === 'addon-5-keap-legacy-plusthis-premium-trans-m2m') {
              quantity = plusThisPremiumQtyGraphlyM2m
            }
            return {
              code,
              quantity,
            }
          }),
      })
    }

    const thryvMaxAddonsAnnualApp = apps.find(
      ({code}) =>
        code === 'addon-5-keap-addons-annual' ||
        code === 'addon-5-keap-addons-legacy-annual',
    )
    const thryvMaxAddonproductsAnnual = all5PlanPrices.apps
      .filter(
        app =>
          (app.parentProduct === 'addon-5-keap-addons-annual' ||
            app.parentProduct === 'addon-5-keap-addons-legacy-annual') &&
          app.code != 'addon-5-keap-addons-annual' &&
          app.code != 'addon-5-keap-addons-legacy-annual',
      )
      .map(item => item.code)

    if (thryvMaxAddonsAnnualApp) {
      childSubscriptions.push({
        code: thryvMaxAddonsAnnualApp.code,
        quantity: 1,
        addons: apps
          .filter(item => thryvMaxAddonproductsAnnual.includes(item.code))
          .map(({code, quantity = 1}) => {
            if (code === 'addon-5-keap-addcontacts-annual') {
              quantity = keapAddContactsQtyAnnual
            }
            if (code === 'addon-5-keap-textvoice-annual') {
              quantity = keapTextVoiceQtyAnnual
            }
            if (code === 'addon-5-keap-legacy-graphly-contacts-annual') {
              quantity = keapAddContactsQtyGraphlyAnnual
            }
            if (code === 'addon-5-keap-legacy-plusthis-premium-trans-annual') {
              quantity = plusThisPremiumQtyGraphlyAnnual
            }
            return {
              code,
              quantity,
            }
          }),
      })
    }

    childSubscriptions.push({
      code: marketplace.code,
      quantity: 1,
      addons: apps
        .filter(({name, code}) => {
          if (thryvMaxAddonproducts.includes(code)) return false
          if (thryvMaxAddonproductsAnnual.includes(code)) return false
          if (code === 'proservices-m2m' || code === 'tp_add-5-thryv-m2m') {
            return false
          }
          if (code === 'addon-5-mktproeditor' && isBCUnlimitedInOrder) {
            return false
          }
          if (
            code === 'maxhvac-5-thryv-m2m' ||
            code === 'maxroof-5-thryv-m2m' ||
            code === 'maxelect-5-thryv-m2m' ||
            code === 'maxplumb-5-thryv-m2m' ||
            code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
            code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
            code === 'vert_clientsvcs-5-thryv-m2m' ||
            code === 'vert_svcs-5-thryv-m2m' ||
            code === 'appmarket-5-thryv-m2m' ||
            code === 'vert_mc_clientsvcs-5-thryv-ot' ||
            code === 'vert_mc_web_clientsvcs-5-thryv-ot' ||
            code === 'vert_clientsvcs-5-thryv-ot' ||
            code === 'addon-5-keap-addons' ||
            code === 'addon-5-keap-addons-legacy-m2m' ||
            code === 'addon-5-keap-addons-annual' ||
            code === 'addon-5-keap-addons-legacy-annual' ||
            code === 'tp_add-5-thryv-m2m' ||
            code === '247support-m2m' ||
            code === 'addon-5-listrefrsh-m2m' ||
            code === 'addon-5-stratcontent-m2m' ||
            code === 'addon-5-mktgsvcs-ypcom-m2m' ||
            code === 'addon-5-mktgsvcs-ypcom-annual' ||
            code === 'addon-5-mktgsvcs-soci-m2m' ||
            code === 'addon-5-mktgsvcs-soci-annual' ||
            code === 'addon-ctn-bninat-m2m' ||
            code === 'addon-ctn-bninat-annual'
            // code === 'addon-5-keap-partner-cert-sandbox-fee-m2m'
            // code === 'addon-5-keap-custdfy-ot'
          ) {
            return false
          }
          const isListingsApp = code === 'app-listings'
          if (isListingsApp && isBCorMCInCart && frontendListingsBcMc)
            return false
          return (
            name !== 'AEO & SEO' &&
            name !== 'SEO Keywords' &&
            name !== 'AEO & SEO PowerBoost' &&
            name !== 'SEO Non-Guaranteed' &&
            name !== 'SEO Money Back Guarantee' &&
            name !== 'Thryv Leads' &&
            // name !== 'Keap Max' &&
            name !== 'Leads Lift BNI' &&
            name !== 'Leads Lift' &&
            !code.startsWith('tl-')
          )
        })
        .map(({code, quantity = 1}) => {
          // if (isNewTLMCAccessible) {
          //   if (code.includes('tl-5-')) {
          //     code = 'tl-mc-5-thryv-m2m'
          //   }
          //   if (code.includes('addon-4-tl')) {
          //     code = 'addon-4-tl-mc-m2m'
          //   }
          // } else {
          //   if (code.includes('tl-5-')) {
          //     code = 'tl-5-thryv-m2m'
          //   }
          //   if (code.includes('addon-4-tl')) {
          //     code = 'addon-4-tl-m2m'
          //   }
          // }
          return {
            code,
            quantity,
          }
        })
        .filter(
          (addon, idx, arr) =>
            arr.findIndex(a => a.code === addon.code) === idx,
        ),
    })

    let isLeadLiftExist = apps.find(
      item => item.code === 'addon-5-leadslift-m2m',
    )
    // FULSAS-11318: was AU-only; NZ never got Leads Lift into
    // childSubscriptions here even though it showed in the payment preview.
    const isLeadLiftEligibleCountry = ['AU', 'NZ'].includes(
      accountInfo.billingAddress.country,
    )

    if (isLeadLiftExist && isLeadLiftEligibleCountry) {
      childSubscriptions.push({
        code: isLeadLiftExist.code,
        quantity: isLeadLiftExist.quantity || 1,
        addons: [],
      })
    }

    let isLeadLiftBniExist = apps.find(
      item => item.code === 'addon-5-leadslift-bni-m2m',
    )
    if (isLeadLiftBniExist) {
      childSubscriptions.push({
        code: isLeadLiftBniExist.code,
        quantity: isLeadLiftBniExist.quantity || 1,
        addons: [],
      })
    }

    const ctnApp = apps.find(
      ({code}) =>
        code === 'addon-ctn-bninat-m2m' || code === 'addon-ctn-bninat-annual',
    )
    if (ctnApp) {
      childSubscriptions.push({
        code: ctnApp.code,
        quantity: ctnApp.quantity || 1,
        addons: [],
      })
    }

    const mktgSvcsCodes = [
      'addon-5-mktgsvcs-ypcom-m2m',
      'addon-5-mktgsvcs-ypcom-annual',
      'addon-5-mktgsvcs-soci-m2m',
      'addon-5-mktgsvcs-soci-annual',
    ]
    for (const mktgSvcsCode of mktgSvcsCodes) {
      const mktgSvcsApp = apps.find(item => item.code === mktgSvcsCode)
      if (mktgSvcsApp) {
        childSubscriptions.push({
          code: mktgSvcsApp.code,
          quantity: mktgSvcsApp.quantity || 1,
          addons: [],
        })
      }
    }

    const oneTimeFees = oneTimes.map(({code, quantity = 1}) => {
      return {
        code,
        quantity,
      }
    })

    // Hanlde new Alpha SEO
    const newAlphaSEO = apps.find(({name}) => name === 'AEO & SEO')

    if (newAlphaSEO) {
      const keyword = apps.find(({name}) => name === 'SEO Keywords')
      const powerboost = apps.find(({name}) => name === 'AEO & SEO PowerBoost')
      const seoStartDate =
        newAlphaSEO.startDate || newAlphaSEO.configuredOptions?.billingDate
      const seoSubscription = {
        code: newAlphaSEO.code,
        quantity: newAlphaSEO.amount || newAlphaSEO.quantity || 1,
        addons: [],
        ...(seoStartDate &&
          new Date(seoStartDate) >= new Date() && {startDate: seoStartDate}),
      }

      if (keyword) {
        seoSubscription.addons.push({
          code: keyword.code,
          quantity: keyword.quantity,
        })
      }

      if (powerboost) {
        seoSubscription.addons.push({
          code: powerboost.code,
          quantity: powerboost.quantity,
        })
      }

      childSubscriptions.push(seoSubscription)
    }

    if (newAlphaSEO) {
      for (let subscription of childSubscriptions) {
        if (
          subscription.code === 'seobase-5-thryv-m2m' &&
          subscription.quantity === 1
        ) {
          subscription.quantity = newAlphaSEO.amount
          subscription.amount = 1
        }
      }
    }

    // Check if any app is standalone to add it as a subscription
    for (let app of apps) {
      if (
        (app?.name.includes('SEO Non-Guaranteed') ||
          app?.name.includes('SEO Money Back Guarantee')) &&
        !newAlphaSEO
        // app?.name === 'Keap Max'
      ) {
        const formattedApp = formatApps(apps, ({name}) =>
          name.includes(app?.name),
        )
        childSubscriptions.push(formattedApp[0])
      } else if (app?.name === 'Thryv Leads' || app?.code.startsWith('tl-')) {
        const tlStartDate = app.startDate || app.configuredOptions?.billingDate
        childSubscriptions.push({
          code: app.code,
          quantity: app.quantity ?? 1,
          addons: [],
          ...(tlStartDate &&
            new Date(tlStartDate) >= new Date() && {startDate: tlStartDate}),
        })
      } else if (app?.code === 'tp_add-5-thryv-m2m') {
        const formattedApp = formatApps(apps, ({name}) =>
          name.includes(app?.name),
        )
        childSubscriptions.push(formattedApp[0])
      } else if (app?.code === '247support-m2m') {
        const formattedApp = formatApps(apps, ({name}) =>
          name.includes(app?.name),
        )
        childSubscriptions.push(formattedApp[0])
      } else if (app?.code === 'proservices-m2m') {
        const formattedApp = formatApps(apps, ({name}) =>
          name.includes(app?.name),
        )
        childSubscriptions.push(formattedApp[0])
      } else if (
        app?.code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_mc_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_mc_web_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_svcs-5-thryv-m2m' ||
        app?.code === 'addon-5-listrefrsh-m2m' ||
        app?.code === 'addon-5-stratcontent-m2m'
        // app?.code === 'addon-5-keap-custdfy-ot'
      ) {
        const formattedApp = formatApps(apps, ({code}) => code === app?.code)
        childSubscriptions.push(formattedApp[0])
      }
    }

    const isDesignFeeWaived = isWebsiteValid
      ? oneTimeFees.filter(oneTimeFee => oneTimeFee.code !== 'thryvdesg2')
      : oneTimeFees

    const hasZapier = childSubscriptions?.find(item =>
      item?.addons?.find(addOn => addOn.code === 'kp-zapier-750zaps-m2m'),
    )

    if (hasZapier) {
      childSubscriptions = childSubscriptions?.map(item => {
        if (
          item.addons?.find(addOn => addOn.code === 'kp-zapier-750zaps-m2m')
        ) {
          item.addons = item.addons?.filter(
            addOn => addOn.code !== 'kp-zapier-750zaps-m2m',
          )
        }
        return item
      })
    }
    console.log('AccountInfo --cc =======> upgrade order', accountInfo)

    const data = {
      firstName: accountInfo.recurlyFirst,
      lastName: accountInfo.recurlyLast,
      email: accountInfo.recurlyEmail,
      company: accountInfo.recurlyCompany,
      currency:
        CC_CURRENCIES[COUNTRIES[accountInfo.billingAddress.country]?.value],
      industryId: order.template || accountInfo.industry,
      businessAddress: {
        street: accountInfo.billingAddress.street1,
        city: accountInfo.billingAddress.city,
        state:
          accountInfo.billingAddress.region || accountInfo.billingAddress.state,
        postalCode:
          accountInfo.billingAddress.postalCode ||
          accountInfo.billingAddress.zipCode,
        country: accountInfo.billingAddress.country,
        phone: accountInfo.billingAddress.phone,
      },
      contactPhone: contactInfo,
      thryvId: order?.CCAccountInfo?.thryvId || accountInfo?.thryvID,
      flow: 'upgrade',
      sendLinkVia: contactMethod,
      parentSubscriptions,
      childSubscriptions,
      oneTimeFees: isDesignFeeWaived,
      coupons: ccUpgradeCoupons || [],
      isCohort,
      keapAccount: keapAccount || accountInfo?.keapAccount || false,
      keapId: keapID || '',
    }
    if (cohortEaid) {
      data.cohortEaid = cohortEaid
    }
    // On-behalf-of Sales Rep: prefer the rep set in the upgrade screen's own
    // edit modal (accountInfo), fall back to the one carried on the order from
    // the account-management edit-location modal (buildV5UpgradeOrder). Either
    // way behalfRepId → cc_order.behalf_rep_id → Recurly On_Behalf_Of.
    const behalfSource = accountInfo?.salesRepCode ? accountInfo : order
    if (behalfSource?.salesRepCode) {
      data.behalfRepId = behalfSource.salesRepCode
      if (behalfSource.behalfFirst) {
        data.behalfFirstName = behalfSource.behalfFirst
      }
      if (behalfSource.behalfLast) {
        data.behalfLastName = behalfSource.behalfLast
      }
      if (behalfSource.behalfEmail) {
        data.behalfEmail = behalfSource.behalfEmail
      }
    }
    if (accountInfo?.overrideRepCode) {
      data.overrideRepId = accountInfo.overrideRepCode
      if (accountInfo.overrideRepFirst) {
        data.overrideRepFirst = accountInfo.overrideRepFirst
      }
      if (accountInfo.overrideRepLast) {
        data.overrideRepLast = accountInfo.overrideRepLast
      }
      if (accountInfo.overrideRepEmail) {
        data.overrideRepEmail = accountInfo.overrideRepEmail
      }
    }
    // xpCode isn't seeded onto the accountInfo state for v5upgrade navigations
    // (see the useState initializer above) — fall back to where the caller
    // actually attached it: accountSubscriptionData.accountInfo (AllAccounts'
    // withAccountInfoXpCode) or the top-level order (MyAccounts/AllAccounts'
    // buildV5UpgradeOrder), matching the fallback pattern already used for
    // keapAccount/hasPaidOnboardingFee/hasPaidDesignFee above.
    const resolvedXpCode =
      accountInfo?.xpCode ||
      accountSubscriptionData?.accountInfo?.xpCode ||
      order?.xpCode
    if (resolvedXpCode) {
      data.xpCode = resolvedXpCode
    }
    // Which toggle produced resolvedXpCode (keap-partner/legacy-partner) — same
    // fallback chain as xpCode above, so the backend can route it correctly.
    const resolvedSalesRepOrPartner =
      accountInfo?.salesRepOrPartner ||
      accountSubscriptionData?.accountInfo?.salesRepOrPartner ||
      order?.salesRepOrPartner
    if (resolvedSalesRepOrPartner) {
      data.salesRepOrPartner = resolvedSalesRepOrPartner
    }
    if (['AU', 'NZ'].includes(accountInfo.billingAddress.country)) {
      data.abn = accountInfo.abn.replaceAll(' ', '')
      if (!data.abn.length) delete data.abn
    }
    if (kickoffApptId) {
      data.onboardingApptId = kickoffApptId
    }
    if (creativeApptId) {
      data.creativeApptId = creativeApptId
    }
    if (thryvSuccessServicesApptId) {
      data.thryvSuccessServicesApptId = thryvSuccessServicesApptId
    }
    if (thryvLeadsId) {
      data.thryvLeadsId = thryvLeadsId
    }
    if (seoProposal) {
      data.seoProposalId = seoProposal.seoProposalId
      data.seoDomain = seoProposal.seoDomain
    }
    if (alphaSeo) {
      data.seoProposalId = alphaSeo.alphaProposalId
      data.seoDomain = alphaSeo.alphaDomain
      // data.seoCategories = alphaSeo.alphaCategories
      // data.seoGeos = alphaSeo.alphaGeos
    } else if (newAlphaSEO) {
      data.seoProposalId = newAlphaSEO.alphaProposalId
      data.seoDomain = newAlphaSEO.alphaDomain
    }
    if (sensisDomain) {
      data.sensisDomain = sensisDomain
    }
    if (mcDomain) {
      data.mcDomain = mcDomain
    }
    if (tlDomain) {
      data.tlDomain = tlDomain
    }
    if (keapDomain) {
      data.keapDomain = keapDomain
    }
    if (contactMethod === 'tss') {
      data.tssUpgrade = true
    }
    if (selectedPaymentMethod) {
      data.isInvoiceBilled = selectedPaymentMethod === 'Invoice'
    }
    if (allowInvoice && selectedPaymentMethod !== 'Invoice') {
      data.updateToAutopay = true
    }
    if (keapTrailAppId && keapTrailAppId.length > 0) {
      data.keapTrailId = keapTrailAppId
    }
    if (order5NUId) {
      data.orderHeaderId = order5NUId
    }
    console.log('send link data --cc =======> upgrade order', data)
    data.appUrl = window.location.origin

    try {
      const result = await run(client('cc/order/send-link', {data}))
      if (
        result.message === 'Payment link sent' ||
        result.message === 'Order created'
      ) {
        toast.success(result.message)
        updateOrder5Status('send-link', 'Submitted', result)
      } else {
        toast.error(result.message)
        handleSendLinkFailEmail(contactMethod)
      }
      if (result.paymentUuid) {
        return result
      }
    } catch (error) {
      handleSendLinkFailEmail(contactMethod)
      setProcessing(false)
      if (error.status !== 400) {
        toast.error(`Error sending link: ${error.message}`)
        throw error
      }
      if (error.errors) {
        error.errors.forEach(({message}) => toast.error(message))
      } else {
        toast.error(`Error sending link: ${error.message}`)
      }
    }
  }

  const resendPaymentLink = async current => {
    console.log('AccountInfo --resend =======> upgrade order', accountInfo)

    // OOE-UI-FF/OOE-UI-CG: the optional chain below kept this from throwing,
    // but it still sent `orderId: undefined` and the request came back as a
    // 400 '"body.orderId" is required' that surfaced as an unhandled
    // rejection. Don't make the call we already know the API will reject.
    if (!current?.orderId) {
      toast.error('Error resending link')
      handleSendLinkFailEmail(current?.method)
      return
    }

    try {
      const data = {
        orderId: current?.orderId?.toString(),
        country: COUNTRIES[accountInfo.billingAddress.country]?.isoValue,
        method: current.method,
        email: current.email,
        orderHeaderId: order5NUId,
      }
      if (isCCPlan) {
        data.contactPhone = current.mobile
      } else {
        data.mobile = current.mobile
      }

      console.log('resend link data ====> upgrade order', data)
      data.appUrl = window.location.origin

      // OOE-UI-AE: client() resolves undefined for 204/empty-201 responses, so
      // destructuring the result directly threw "Cannot destructure property
      // 'newUuid'" before the `if (newUuid)` check could run. `|| {}` sends
      // those responses down the existing "Error resending link" branch.
      const {newUuid, shortenedUrl} =
        (await run(
          client(
            !isCCPlan ? 'orders/process/resend-link' : 'cc/order/resend-link',
            {data, method: 'PATCH'},
          ),
        )) || {}
      if (newUuid) {
        toast.success('Payment Link resent ')
        return {updatedUuid: newUuid, shortenedUrl}
      } else {
        toast.error('Error resending link')
        handleSendLinkFailEmail(current.method)
      }
    } catch (error) {
      toast.error('Error resending link')
      handleSendLinkFailEmail(current.method)

      throw error
    }
  }

  const checkClientEmailStatus = async current => {
    const data = {
      email: current.email,
    }

    try {
      const result = await run(
        client(`orders/email-status/${data.email}`, {method: 'GET'}),
      )

      if (result.haswarningorError) {
        toast.error(result.error_message)
      }
      if (
        result.haswarningorError === false &&
        result.show_error_msg === false
      ) {
        if (result.show_send_sms_btn) {
          setShwoSmsOption(true)
        }
      } else {
        setShwoSmsOption(false)
      }
    } catch (err) {
      toast.error(err.error)
    }
  }

  const updateAddressInfo = (data, setShowUpdateAddressModal) => {
    let saveData = {
      ...accountInfo,
      recurlyFirst: data.firstName,
      recurlyLast: data.lastName,
      recurlyCompany: data.company,
      salesRepCode: data.salesRepCode,
      salesRepInfo: data.salesRepInfo,
      behalfEmail: data.behalfEmail,
      behalfFirst: data.behalfFirst,
      behalfLast: data.behalfLast,
      xpCode: data.xpCode,
      overrideRepInfo: data.overrideRepInfo,
      overrideRepCode: data.overrideRepCode,
      overrideRepEmail: data.overrideRepEmail,
      overrideRepFirst: data.overrideRepFirst,
      overrideRepLast: data.overrideRepLast,
      abn: data.abn,
      billingAddress: {
        ...accountInfo.billingAddress,
        street1: data.street1,
        city: data.city,
        region: data.region,
        postalCode: data.postalCode,
        phone: data.phone,
        mobile: data.mobile,
      },
      keapAccount: data.keapAccount,
      keapID: data.keapID,
    }
    if (!isAus && !isNZ) {
      delete saveData.abn
    }
    if (!data.salesRepCode) {
      delete saveData.salesRepInfo
      delete saveData.salesRepCode
      delete saveData.behalfEmail
      delete saveData.behalfFirst
      delete saveData.behalfLast
    }
    if (!data.overrideRepCode) {
      delete saveData.overrideRepInfo
      delete saveData.overrideRepCode
      delete saveData.overrideRepEmail
      delete saveData.overrideRepFirst
      delete saveData.overrideRepLast
    }
    setAccountInfo(saveData)
    setOrderData5CU({
      ...orderData5CU,
      accountInfo: {...orderData5CU.accountInfo, ...saveData},
    })
  }

  const updateBillingAddress = data => {
    if (data.selectedCountry) {
      let _currentStep = currentStep
      if (_currentStep === 0) {
        _currentStep = 1
      }
      setAccountInfo({
        ...accountInfo,
        billingAddress: {
          ...accountInfo.billingAddress,
          country: data.selectedCountry.isoValue,
        },
      })
      setOrderData4CU({
        ...orderData4CU,
        accountInfo: {
          ...orderData4CU.accountInfo,
          billingAddress: {
            ...accountInfo.billingAddress,
            country: data.selectedCountry.isoValue,
          },
        },
      })
      console.log('setOrderData4CU', {
        ...orderData4CU,
        accountInfo: {
          ...orderData4CU.accountInfo,
          billingAddress: {
            ...accountInfo.billingAddress,
            country: data.selectedCountry.isoValue,
          },
        },
      })
      setCurrentStep(_currentStep)
      return
    }

    const newAccountInfo = {
      ...accountInfo,
      billingAddress: {
        ...accountInfo.billingAddress,
        street1: data.streetAddress || data.street1,
        city: data.city,
        region: data.state || data.region,
        postalCode: data.zipCode || data.postalCode,
      },
    }

    if (data.streetAddress || data.street1) {
      newAccountInfo.recurlyStreet = data.streetAddress || data.street1
    }
    if (data.phone) {
      newAccountInfo.billingAddress.phone = data.phone
    }
    if (data.company) {
      newAccountInfo.recurlyCompany = data.company
    }
    if (data.firstName) {
      newAccountInfo.recurlyFirst = data.firstName
    }
    if (data.lastName) {
      newAccountInfo.recurlyLast = data.lastName
    }
    if (data.mobile) {
      newAccountInfo.recurlyPhone = data.mobile
    }
    setAccountInfo(newAccountInfo)
    setOrderData4CU({
      ...orderData4CU,
      // accountInfo: {updatedAddressInfo: data, ...newAccountInfo},
      accountInfo: {...orderData4CU.accountInfo, ...newAccountInfo},
    })
    console.log('setOrderData4CU', {
      ...orderData4CU,
      accountInfo: {...orderData4CU.accountInfo, ...newAccountInfo},
    })
  }

  const getLegacyPaymentPreview = useCallback(
    async coupons => {
      let subscriptions = []
      for (let addOn of order.selectedAddons) {
        if (isWebsite4DesignFeePlanCode(addOn?.planCode)) continue
        let subscription = {
          code: addOn?.planCode,
          quantity: addOn?.quantity || 1,
        }
        subscriptions.push(subscription)
      }

      const currency =
        CC_CURRENCIES[
          COUNTRIES[
            order?.selectedCountry?.isoValue ||
              accountInfo?.billingAddress?.country
          ]?.value
        ]
      const data = {
        subscriptions,
        thryvId: accountInfo?.thryvID || order?.thryvId,
        currency,
        coupons: [...(coupons || []), ...(autoCoupons?.map(c => c.code) || [])],
      }
      if (!previewToastShown.current) {
        try {
          const response = await client('orders/preview-payment', {data})
          setUpgradePaymentPreview(response)
          setPreviewRequestFinished(true)
        } catch (err) {
          if (err.message === 'Billing info missing on account') {
            setUpgradePaymentPreview(null)
            setPreviewRequestFinished(true)
          }
        }
      }
    },
    [order, accountInfo, autoCoupons, client, setUpgradePaymentPreview],
  )

  const getLegacyPaymentPreviewRef = useRef(getLegacyPaymentPreview)
  getLegacyPaymentPreviewRef.current = getLegacyPaymentPreview

  const selectedAddonsKey = JSON.stringify(
    order?.selectedAddons?.map(a => ({p: a.planCode, q: a.quantity})) ?? [],
  )
  const shouldPreviewLegacyPayment =
    isUpgrade &&
    !order?.selectedCentersAndApps &&
    Boolean(order?.selectedAddons) &&
    currentStep >= 3

  useEffect(() => {
    if (shouldPreviewLegacyPayment) {
      getLegacyPaymentPreviewRef.current()
    }
  }, [shouldPreviewLegacyPayment, currentStep, selectedAddonsKey])

  const getPaymentPreview = async (
    orderData = order,
    coupons = ccUpgradeCoupons,
  ) => {
    const {
      selectedCentersAndApps: {
        apps: rawApps = [],
        centers = [],
        oneTimes = [],
      } = {},
    } = orderData || {}
    // GEP-6803: same BNI strip as the send-link path. Read-only display only.
    const apps = rawApps.filter(
      ({code}) => !SOCIAL_ADS_BNI_PLAN_CODES.includes(code),
    )
    const isMCInCart = centers.some(({code}) => MC_PLANS.includes(code))
    const isBCInCart = centers.some(({code}) => BC_PLANS.includes(code))
    const parentSubscriptions = formatCenters(centers, ({code}) =>
      code.startsWith('cc_'),
    )
    const marketplace = apps.find(({code}) => code === 'appmarket-5-thryv-m2m')
    let childSubscriptions = []
    for (let center of centers) {
      if (center?.code.startsWith('cc_')) {
        continue
      } else {
        const formattedCenter = formatCenters(
          centers,
          ({code}) => code === center?.code,
        )
        childSubscriptions.push(formattedCenter[0])
      }
    }

    console.log('childSubscriptions - centers :: ', childSubscriptions)

    // Add monthly keap addons either to legacy or new keap addons
    const thryvMaxAddonsMonthlyApp = apps.find(
      ({code}) =>
        code === 'addon-5-keap-addons' ||
        code === 'addon-5-keap-addons-legacy-m2m',
    )
    const thryvMaxAddonproducts = (all5PlanPrices?.apps || [])
      .filter(
        app =>
          (app.parentProduct === 'addon-5-keap-addons' ||
            app.parentProduct === 'addon-5-keap-addons-legacy-m2m') &&
          app.code != 'addon-5-keap-addons' &&
          app.code != 'addon-5-keap-addons-legacy-m2m',
      )
      .map(item => item.code)
    if (thryvMaxAddonsMonthlyApp) {
      //Keap Add Ons subscription
      const hasExplicitKeapAddContactsQty =
        keapAddContactsQty !== '' &&
        keapAddContactsQty !== null &&
        keapAddContactsQty !== undefined

      childSubscriptions.push({
        code: thryvMaxAddonsMonthlyApp.code,
        quantity: 1,
        addons: apps
          .filter(item => thryvMaxAddonproducts.includes(item.code))
          .map(({code, quantity}) => {
            if (code === 'addon-5-keap-textvoice-m2m') {
              quantity = keapTextVoiceQty
            }
            if (
              code === 'addon-5-keap-addcontacts-m2m' &&
              hasExplicitKeapAddContactsQty
            ) {
              quantity = keapAddContactsQty
            }
            if (code === 'addon-5-keap-legacy-graphly-contacts-m2m') {
              quantity = keapAddContactsQtyGraphlyM2m
            }
            if (code === 'addon-5-keap-legacy-plusthis-premium-trans-m2m') {
              quantity = plusThisPremiumQtyGraphlyM2m
            }
            return {
              code,
              quantity,
            }
          }),
      })
    }
    console.log(
      'childSubscriptions  with keap monthly products : ',
      childSubscriptions,
    )

    // Add annual keap addons either to legacy or new keap addons
    const thryvMaxAddonsAnnualApp = apps.find(
      ({code}) =>
        code === 'addon-5-keap-addons-annual' ||
        code === 'addon-5-keap-addons-legacy-annual',
    )
    const thryvMaxAddonproductsAnnual = all5PlanPrices.apps
      .filter(
        app =>
          (app.parentProduct === 'addon-5-keap-addons-annual' ||
            app.parentProduct === 'addon-5-keap-addons-legacy-annual') &&
          app.code != 'addon-5-keap-addons-annual' &&
          app.code != 'addon-5-keap-addons-legacy-annual',
      )
      .map(item => item.code)

    if (thryvMaxAddonsAnnualApp) {
      childSubscriptions.push({
        code: thryvMaxAddonsAnnualApp.code,
        quantity: 1,
        addons: apps
          .filter(item => thryvMaxAddonproductsAnnual.includes(item.code))
          .map(({code, quantity = 1}) => {
            if (code === 'addon-5-keap-addcontacts-annual') {
              quantity = keapAddContactsQtyAnnual
            }
            if (code === 'addon-5-keap-textvoice-annual') {
              quantity = keapTextVoiceQtyAnnual
            }
            if (code === 'addon-5-keap-legacy-graphly-contacts-annual') {
              quantity = keapAddContactsQtyGraphlyAnnual
            }
            if (code === 'addon-5-keap-legacy-plusthis-premium-trans-annual') {
              quantity = plusThisPremiumQtyGraphlyAnnual
            }
            return {
              code,
              quantity,
            }
          }),
      })
    }
    console.log(
      'childSubscriptions  with keap annual products : ',
      childSubscriptions,
    )

    // Add thryv addons either to app-market subscription
    childSubscriptions.push({
      code: marketplace.code,
      quantity: 1,
      addons: apps
        .filter(({code}) => {
          if (thryvMaxAddonproducts.includes(code)) return false
          if (thryvMaxAddonproductsAnnual.includes(code)) return false
          const isListingsApp = code === 'app-listings'
          if (
            isListingsApp &&
            (isMCInCart || isBCInCart) &&
            frontendListingsBcMc
          ) {
            return false
          }
          if (
            code === 'maxhvac-5-thryv-m2m' ||
            code === 'maxroof-5-thryv-m2m' ||
            code === 'maxelect-5-thryv-m2m' ||
            code === 'maxplumb-5-thryv-m2m' ||
            code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
            code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
            code === 'vert_clientsvcs-5-thryv-m2m' ||
            code === 'vert_mc_clientsvcs-5-thryv-ot' ||
            code === 'vert_mc_web_clientsvcs-5-thryv-ot' ||
            code === 'vert_clientsvcs-5-thryv-ot' ||
            code === 'vert_svcs-5-thryv-m2m' ||
            code === 'appmarket-5-thryv-m2m' ||
            code === 'addon-5-keap-addons' ||
            code === 'addon-5-keap-addons-legacy-m2m' ||
            code === 'addon-5-keap-addons-annual' ||
            code === 'addon-5-keap-addons-legacy-annual' ||
            code === 'addon-5-leadslift-m2m' ||
            code === 'addon-5-leadslift-bni-m2m' ||
            code === 'addon-5-visibilityboost' ||
            code === 'tp_add-5-thryv-m2m' ||
            code === '247support-m2m' ||
            code === 'addon-ctn-bninat-m2m' ||
            code === 'addon-ctn-bninat-annual'
            // code === 'addon-5-keap-partner-cert-sandbox-fee-m2m'
          ) {
            return false
          }
          if (isNewTLMCAccessible) {
            let isTLLeadExist =
              centers.find(({code}) =>
                [
                  'tl-5-thryv-m2m',
                  'tl-mc-5-thryv-m2m',
                  'tl-tax-5-thryv-m2m',
                ].includes(code),
              ) ||
              apps.find(({code}) =>
                [
                  'tl-5-thryv-m2m',
                  'tl-mc-5-thryv-m2m',
                  'tl-tax-5-thryv-m2m',
                ].includes(code),
              )
            if (isTLLeadExist) {
              if (GROWTH_PACKAGES.includes(code)) {
                return false
              }
            }
          }
          return (
            !['addon-5-seokey-m2m', 'addon-5-seopb-m2m'].includes(code) &&
            !['seo-5-thryv-m2m', 'seombg-5-thryv-m2m'].includes(code) &&
            code !== 'seobase-5-thryv-m2m' &&
            code !== 'tl-mc-5-thryv-m2m' &&
            code !== 'tl-5-thryv-m2m' &&
            code !== 'tl-tax-5-thryv-m2m' &&
            // name !== 'Keap Max' &&
            code !== 'proservices-m2m'
          )
        })
        .map(({code, quantity = 1}) => {
          return {
            code,
            quantity,
          }
        })
        .filter(
          (addon, idx, arr) =>
            arr.findIndex(a => a.code === addon.code) === idx,
        ),
    })
    console.log('childSubscriptions with thryv addons :: ', childSubscriptions)

    const oneTimeFees = oneTimes.map(({code, quantity = 1}) => {
      return {
        code,
        quantity,
      }
    })

    // Hanlde new Alpha SEO
    const newAlphaSEO =
      centers.find(({code}) => code === 'seobase-5-thryv-m2m') ||
      apps.find(({code}) => code === 'seobase-5-thryv-m2m')
    const keyword = apps.find(({code}) => code === 'addon-5-seokey-m2m')
    const powerboost = apps.find(({code}) => code === 'addon-5-seopb-m2m')

    if (newAlphaSEO) {
      const seoStartDate =
        newAlphaSEO.startDate || newAlphaSEO.configuredOptions?.billingDate
      const seoSubscription = {
        code: newAlphaSEO.code,
        quantity: newAlphaSEO.amount || newAlphaSEO.quantity || 1,
        addons: [],
        ...(seoStartDate &&
          new Date(seoStartDate) >= new Date() && {startDate: seoStartDate}),
      }

      if (keyword) {
        seoSubscription.addons.push({
          code: keyword.code,
          quantity: keyword.quantity,
        })
      }

      if (powerboost) {
        seoSubscription.addons.push({
          code: powerboost.code,
          quantity: powerboost.quantity,
        })
      }

      childSubscriptions.push(seoSubscription)
    }
    console.log('childSubscriptions 4', childSubscriptions)

    if (newAlphaSEO) {
      for (let subscription of childSubscriptions) {
        if (
          subscription.code === 'seobase-5-thryv-m2m' &&
          subscription.quantity === 1
        ) {
          subscription.quantity = newAlphaSEO.amount
          subscription.amount = 1
        }
      }
    }

    for (let app of apps) {
      if (
        ((app?.code === 'seo-5-thryv-m2m' ||
          app?.code === 'seombg-5-thryv-m2m') &&
          !newAlphaSEO) ||
        // app?.name === 'Keap Max' ||
        app?.code === 'addon-5-leadslift-m2m' ||
        app?.code === 'addon-5-leadslift-bni-m2m'
      ) {
        const formattedApp = formatApps(apps, ({code}) => code === app?.code)
        childSubscriptions.push(formattedApp[0])
      } else if (
        ['tl-5-thryv-m2m', 'tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m'].includes(
          app?.code,
        )
      ) {
        const tlStartDate = app.startDate || app.configuredOptions?.billingDate
        childSubscriptions.push({
          code: app.code,
          quantity: app.quantity ?? 1,
          addons: [],
          ...(tlStartDate &&
            new Date(tlStartDate) >= new Date() && {startDate: tlStartDate}),
        })
      } else if (
        app?.code === 'addon-ctn-bninat-m2m' ||
        app?.code === 'addon-ctn-bninat-annual'
      ) {
        childSubscriptions.push({
          code: app.code,
          quantity: app.quantity ?? 1,
          addons: [],
        })
      } else if (
        app?.code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_mc_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_mc_web_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_svcs-5-thryv-m2m' ||
        app?.code === 'proservices-m2m'
        // app?.code === 'addon-5-keap-custdfy-ot'
      ) {
        const formattedApp = formatApps(apps, ({code}) => code === app?.code)
        childSubscriptions.push(formattedApp[0])
      } else if (app?.code === 'addon-5-visibilityboost') {
        childSubscriptions.push({
          code: app.code,
          quantity: app.quantity ?? 1,
          addons: (app.entitlements || []).map(({code, quantity}) => ({
            code,
            quantity: quantity ?? 1,
          })),
        })
      }
    }
    console.log('childSubscriptions 5', childSubscriptions)

    const currency =
      CC_CURRENCIES[COUNTRIES[accountInfo.billingAddress.country]?.value]
    const data = {
      childSubscriptions,
      parentSubscriptions,
      oneTimeFees,
      thryvId: accountInfo?.thryvID || order?.CCAccountInfo?.thryvId,
      currency,
      coupons: coupons || [],
    }
    let isNewSEOExist = childSubscriptions.find(
      ({code}) => code === 'seobase-5-thryv-m2m',
    )
    const hasZapier =
      childSubscriptions ||
      []?.find(item =>
        item?.addons?.find(addOn => addOn.code === 'kp-zapier-750zaps-m2m'),
      )

    if (hasZapier) {
      data.childSubscriptions?.map(item => {
        if (item.code === 'appmarket-5-thryv-m2m') {
          let addons = (item.addons || []).filter(subAddon => {
            return !['kp-zapier-750zaps-m2m'].includes(subAddon.code)
          })
          console.log(item.addons)
          item.addons = addons
        }
        return item
      })
    }

    if (isNewSEOExist) {
      data.childSubscriptions?.map(item => {
        if (item.code === 'appmarket-5-thryv-m2m') {
          let addons = (item.addons || []).filter(subAddons => {
            return !['seo-5-thryv-m2m', 'seombg-5-thryv-m2m'].includes(
              subAddons.code,
            )
          })
          console.log(item.addons)
          item.addons = addons
        }
        return item
      })
    }

    if (currency === 'AUD') {
      const hasKCP = data.oneTimeFees?.find(
        item => item.code === 'keap_kcp-cert-5-fee',
      )

      if (hasKCP) {
        data.oneTimeFees = data.oneTimeFees?.map(item => {
          if (item.code === 'keap_kcp-cert-5-fee') {
            let matchedQty = KCP_AU_QTY_MAPPING.find(
              mapItem => Number(mapItem.amount) === Number(item.quantity),
            )
            return {
              ...item,
              quantity: matchedQty?.qty,
            }
          } else return item
        })
      }
    }
    if (!previewToastShown.current) {
      try {
        const response = await client('cc/order/preview-payment', {data})
        // Build the final object first, dispatch once. Mutating `response`
        // after dispatch would mutate Redux state outside a reducer and
        // (with shallow useSelector) skip dependent re-renders.
        // Array.prototype.includes only takes one needle; the original
        // form silently dropped 25month2 and 25month3 detection.
        const isTransitionIncentiveCoupon = coupons
          ? coupons.some(
              x =>
                x.includes('25month1') ||
                x.includes('25month2') ||
                x.includes('25month3'),
            )
          : false
        setUpgradePaymentPreview({...response, isTransitionIncentiveCoupon})
      } catch (err) {
        // Always clear the preview on error so the cart never renders a
        // stale total from a prior call after a failed request.
        setUpgradePaymentPreview(null)
        if (err.message !== 'Billing info missing on account') {
          // Surface non-billing-info errors so they don't fail silently.
          Sentry.captureException(err, {
            tags: {area: 'upgrade-payment-preview'},
          })
          //  toast.error('Unable to load payment preview. Please retry.')
        }
      } finally {
        // Flip loading regardless of outcome so the cart preview never
        // gets stuck on the skeleton when the response path or any error
        // skips the success setter.
        setPreviewRequestFinished(true)
      }
    }
  }

  const captureOrder5NewUpgrade = async (context, orderFlow, contextData) => {
    try {
      console.log('captureOrder5NewUpgrade() - context', context)
      // Guard: Don't create order if it's already being created or already exists
      if (context === 'catalog-continue-summary') {
        if (order5NUId) {
          console.log('Order already exists, skipping duplicate creation')
          return
        }
        if (isCreatingOrder.current) {
          console.log(
            'Order creation already in progress, skipping duplicate call',
          )
          return
        }
        isCreatingOrder.current = true
      }
      console.log('captureOrder5NewUpgrade() - orderFlow', orderFlow)
      console.log('captureOrder5NewUpgrade() - contextData', contextData)
      console.log('captureOrder5NewUpgrade() - orderData5CU', orderData5CU)
      console.log(
        'captureOrder5NewUpgrade() - ccUpgradeCoupons',
        ccUpgradeCoupons,
      )

      const toStr = v => (v === undefined || v === null ? null : `${v}`)

      const data = {
        order_status: 'Pending',
        order_version: '5.0',
        order_flow: 'Update',
        // order_submitted_date: new Date(),
        order_submitted_by: 'system', // was 'null' string
        order_completed_date: null,

        industry_id: toStr(orderData5CU.order?.industryCategoryId),
        industry_description: orderData5CU.order?.industryCategoryDescription,
        country_code:
          orderData5CU.order?.selectedCountry?.isoValue ||
          orderData5CU.accountInfo?.billingInfo?.country ||
          'US',

        account_email: orderData5CU.accountInfo?.email,
        contact_first_name: orderData5CU.accountInfo?.firstName,
        contact_last_name: orderData5CU.accountInfo?.lastName,

        contact_phone:
          toStr(orderData5CU.accountInfo?.billingAddress?.phone) ||
          toStr(orderData5CU.accountInfo?.recurlyPhone) ||
          toStr(orderData5CU.accountInfo?.companyPhone),

        contact_mobile:
          toStr(orderData5CU.accountInfo?.mobile) ||
          toStr(orderData5CU.accountInfo?.billingAddress?.mobile) ||
          toStr(orderData5CU.accountInfo?.recurlyPhone) ||
          toStr(orderData5CU.accountInfo?.billingAddress?.phone),

        contact_email: orderData5CU.accountInfo?.email,
        company_name:
          orderData5CU.accountInfo?.company ||
          orderData5CU.accountInfo?.recurlyCompany ||
          '',

        company_address1:
          orderData5CU.accountInfo?.billingAddress?.street1 ??
          orderData5CU.accountInfo?.businessAddress?.street1,
        company_address2:
          orderData5CU.accountInfo?.billingAddress?.street2 ??
          orderData5CU.accountInfo?.businessAddress?.street2,
        company_city:
          orderData5CU.accountInfo?.billingAddress?.city ??
          orderData5CU.accountInfo?.businessAddress?.city,
        company_state:
          orderData5CU.accountInfo?.billingAddress?.region ??
          orderData5CU.accountInfo?.billingAddress?.state ??
          orderData5CU.accountInfo?.businessAddress?.region ??
          '',
        company_zipcode:
          orderData5CU.accountInfo?.billingAddress?.postalCode ??
          orderData5CU.accountInfo?.billingAddress?.zipCode ??
          orderData5CU.accountInfo?.businessAddress?.postalCode ??
          '',

        company_phone:
          toStr(orderData5CU.accountInfo?.companyPhone) ||
          toStr(orderData5CU.accountInfo?.billingAddress?.phone) ||
          toStr(orderData5CU.accountInfo?.recurlyPhone),

        sales_rep_id: orderData5CU.accountInfo?.salesRepCode ?? null,
        on_behalf_of: orderData5CU.accountInfo?.behalfEmail ?? null,
        xp_code: orderData5CU.accountInfo?.xpCode ?? null,
        abn: orderData5CU.accountInfo?.abn ?? null,

        thryv_id: orderData5CU.order?.CCAccountInfo?.thryvId,

        account_code:
          orderData5CU.recurlyAccount?.code ??
          orderData5CU.order?.CCAccountInfo?.ccId ??
          null, // was 'null' string

        eaid: orderData5CU.accountInfo?.eaid || 'unknown',

        is_invoice_billed: orderData5CU.accountInfo?.allowInvoice ? 1 : 0,
        is_cohort: null,
        demo_lead_id: null,
        no_service: null,

        onboarding_appt_id: order5OnboardingApptId,
        creative_appt_id: order5CreativeApptId,
        thryv_success_services_appt_id: order5ThryvSuccessServicesApptId,

        update_to_autopay: 1,
        on_boarding_fee: 0,

        purchase_intent_1: null,
        purchase_intent_2: null,
        purchase_intent_3: null,
        sensis_site: null,

        lines: [],
        discounts: [],
        reseller: [],
      }

      if (context === 'catalog-continue-summary') {
        data.order_status = 'Pending'
        data.lines = contextData
        data.discounts = order5LinesDiscounts
        setOrder5SelectedLines(contextData)
      } else if (context === 'capture-line-discounts') {
        data.order_status = 'Pending'
        data.lines = order5SelectedLines
        data.discounts = contextData
      } else if (context === 'capture-appointment') {
        console.log(
          'order5SelectedLines - upgrade - capture-appointment',
          order5SelectedLines,
        )
        console.log(
          'order5LinesDiscounts - upgrade - capture-appointment',
          order5LinesDiscounts,
        )
        if (contextData.appointment_type === 'creative') {
          data.creative_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'onboarding') {
          data.onboarding_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'thryv-success-services') {
          data.thryv_success_services_appt_id = contextData.apptId
        }
        data.lines = order5SelectedLines
        data.discounts = order5LinesDiscounts
      } else {
        console.log(
          'order5SelectedLines - upgrade - other context',
          order5SelectedLines,
        )
        console.log(
          'order5LinesDiscounts - upgrade - other context',
          order5LinesDiscounts,
        )
        data.lines = order5SelectedLines
        data.discounts = order5LinesDiscounts
      }
      data.keap_account = keapAccount || false

      if (isReseller) {
        const resellerData = getResellerData(data)
        console.log('resellerData', resellerData)
        if (resellerData) {
          data.reseller.push(resellerData)
        }
      }

      data.lines = (data.lines || []).filter(
        line => line.plansv5_id !== 'kp-zapier-750zaps-m2m',
      )
      if (!order5NUId) {
        console.log('createOrderData - upgrade', data)
        console.log('createOrderData - upgrade (company fields)', {
          company_name: data.company_name,
          company_state: data.company_state,
          company_zipcode: data.company_zipcode,
        })
        const result = await run(
          client('ordercapture/services/createOrder', {data, method: 'POST'}),
        )
        console.log('order created - upgrade', result)
        setOrder5NUId(result.order_id)
        isCreatingOrder.current = false
      } else {
        data.order_id = order5NUId
        console.log('UpdateOrderData - upgrade', data)
        const result = await run(
          client('ordercapture/services/updateOrder', {data, method: 'POST'}),
        )
        console.log('order updated - upgrade', result)
      }
    } catch (error) {
      if (error.status !== 400) {
        console.log(`Error creating/updating order: ${error.message}`)
      }
      if (error.errors) {
        // error.errors.forEach(({message}) => toast.error(message))
        error.errors.forEach(({message}) => console.log(message))
      } else {
        console.log(`Error creating/updating order: ${error.message}`)
      }
      isCreatingOrder.current = false
    }
  }

  const getResellerData = data => {
    if (data.lines && data.lines.length > 0) {
      const nonCcProducts = [
        'cc_basicpls-5-thryv-m2m',
        'appmarket-5-thryv-m2m',
        'addon-5-signatures_usage',
        'app-websitebuilder',
      ]
      const isnonCCPrduct = data.lines.every(line =>
        nonCcProducts.some(prdct => prdct === line.saas_product_code),
      )
      console.log('isnonCCPrduct', isnonCCPrduct)
      if (!isnonCCPrduct) {
        let account_recurly_subscription_id
        if (data?.lines && data.lines.length > 0) {
          for (const line of data.lines) {
            // if (!nonCcProducts.includes(line.saas_product_code)) {
            if (
              line.saas_product_category === 'Center' &&
              line.recurly_subscription_id
            ) {
              account_recurly_subscription_id = line.recurly_subscription_id
              break
            }
            // }
          }
        }
        const resellerData = {
          account_code: orderData5CU.recurlyAccount?.code
            ? orderData5CU.recurlyAccount?.code
            : null,
          account_id: orderData5CU.recurlyAccount?.billingInfo?.accountId
            ? orderData5CU.recurlyAccount?.billingInfo?.accountId
            : orderData5CU.recurlyAccount?.id
            ? orderData5CU.recurlyAccount?.id
            : null,
          no_service: null,
          original_selling_coupon_code: null,
          subscription_id: orderData5CU.order.subscriptionId
            ? orderData5CU.order.subscriptionId
            : account_recurly_subscription_id
            ? account_recurly_subscription_id
            : null,
          thryv_id: orderData5CU.order?.CCAccountInfo?.thryvId,
          uuid: orderData5CU.order?.subscriptionId
            ? orderData5CU.order?.subscriptionId
            : account_recurly_subscription_id
            ? account_recurly_subscription_id
            : null,
        }
        return resellerData
      } else {
        return null
      }
      // return null
    }
  }

  const updateOrder5Status = async (context, orderFlow, contextData) => {
    console.log('updateOrder5Status() - context', context)
    console.log('updateOrder5Status() - orderFlow', orderFlow)
    console.log('updateOrder5Status() - contextData', contextData)
    console.log('captureOrder5NewUpgrade() - orderData5CU', orderData5CU)
    try {
      const statusData = {
        order_id: order5NUId || contextData?.orderId,
        order_status: 'Pending',
        payment_uuid: contextData?.paymentUuid || contextData?.randomId,
      }
      if (context === 'send-link') {
        statusData.order_status = 'Submitted'
      } else if (context === 'cancel-order') {
        statusData.order_status = 'Cancelled'
      }
      const result = await run(
        client('ordercapture/services/updateOrderStatus', {
          data: statusData,
          method: 'PUT',
        }),
      )
      console.log('order updated - upgrade - status', result)
    } catch (error) {
      if (error.status !== 400) {
        console.log(`Error updating order status: ${error.message}`)
        // throw error
      }
      if (error.errors) {
        // error.errors.forEach(({message}) => toast.error(message))
        error.errors.forEach(({message}) => console.log(message))
      } else {
        console.log(`Error updating order status: ${error.message}`)
      }
    }
  }

  const updateApptOnExpire = async (appointment_type, apptId) => {
    const appointmentObj = {
      order_id: order5NUId,
      schedule_status: 'Expired',
      appointment_type: appointment_type,
      appt_id: apptId,
    }
    try {
      // const data = appointmentObj
      const result = await run(
        client('ordercapture/services/updateScheduledAppointment', {
          data: appointmentObj,
          method: 'PUT',
        }),
      )
      if (result.success) {
        if (appointment_type === 'creative') {
          setOrder5CreativeApptId(null)
        } else if (appointment_type === 'onboarding') {
          setOrder5OnboardingApptId(null)
        } else if (appointment_type === 'thryv-success-services') {
          setOrder5ThryvSuccessServicesApptId(null)
        }
        const contextData = {
          appointment_type: appointment_type,
          apptId: null,
        }
        captureOrder5NewUpgrade(
          'capture-appointment',
          'new-upgrade',
          contextData,
        )
      } else {
        console.log(result.message)
      }
    } catch (error) {
      if (error.status !== 400) {
        console.log('error in cancelling Appointment', error)
        // throw error
      }
      if (error.errors) {
        console.log('error in cancelling Appointment', error)
        // throw error
      }
    }
  }

  const captureOrderDiscounts = async discountData => {
    console.log('inside captureOrderDiscounts - ', discountData)
    const allDiscount = []
    let lineCounter = 1
    for (let data of discountData) {
      console.log('disData', data)
      // const today = new Date()
      const dataObj = {
        order_id: order5NUId,
        line_number: lineCounter,
        discount_type: data.type,
        coupon_code: data.code,
        discount_percentage: data.discountPercent,
        discount_amount: data.discountAmount,
        number_of_months: data.durationAmount,
        start_date: new Date(),
        end_date: datePlusDuration(
          new Date(),
          data.durationAmount,
          data.durationUnit,
        ),
        created_date: null,
        create_oprid: null,
        updated_date: null,
        update_oprid: null,
      }
      allDiscount.push(dataObj)
      lineCounter = lineCounter + 1
    }
    setOrder5LinesDiscounts(allDiscount)
    if (allDiscount && allDiscount.length > 0) {
      captureOrder5NewUpgrade(
        'capture-line-discounts',
        'new-upgrade',
        allDiscount,
      )
    }
  }

  function datePlusDuration(date, duration, unit) {
    const newDate = new Date(date)
    if (duration == null || unit == null) return newDate
    if (unit === 'month') newDate.setMonth(newDate.getMonth() + duration)
    if (unit === 'day') newDate.setDate(newDate.getDate() + duration)
    if (unit === 'year') newDate.setFullYear(newDate.getFullYear() + duration)
    return newDate
  }

  const captureOrder4NewUpgrade = async (context, orderFlow, contextData) => {
    try {
      console.log('captureOrder4NewUpgrade() - context', context)
      console.log('captureOrder4NewUpgrade() - orderFlow', orderFlow)
      console.log('captureOrder4NewUpgrade() - contextData', contextData)
      console.log('captureOrder4NewUpgrade() - orderData4CU', orderData4CU)
      console.log(
        'captureOrder4NewUpgrade() - ccUpgradeCoupons',
        ccUpgradeCoupons,
      )

      // The 4.0 upgrade selector stores the selected rep on `order`. The
      // accountInfo object is fetched before that selection and may contain
      // only the account's pre-existing attribution (or no attribution).
      const attribution = {
        ...orderData4CU.accountInfo,
        ...order,
      }

      const data = {
        order_status: 'Pending',
        order_version: '4.0',
        order_flow: 'Update',
        //order_submitted_date: new Date(),
        order_submitted_by: 'system',
        order_completed_date: null,
        industry_id: orderData4CU?.order?.industryCategoryId?.toString(),
        industry_description: orderData4CU.order?.industryCategoryDescription,
        country_code:
          orderData4CU.order?.selectedCountry?.isoValue ||
          orderData4CU.accountInfo?.billingAddress?.country,
        account_email: orderData4CU.accountInfo?.recurlyEmail,
        contact_first_name: orderData4CU.accountInfo?.recurlyFirst,
        contact_last_name: orderData4CU.accountInfo?.recurlyLast,
        contact_phone:
          orderData4CU.accountInfo?.billingAddress?.phone?.toString() ||
          orderData4CU.accountInfo?.recurlyPhone?.toString() ||
          orderData4CU.accountInfo?.companyPhone?.toString(),
        contact_mobile:
          orderData4CU.accountInfo?.billingAddress?.mobile?.toString() ||
          orderData4CU.accountInfo?.recurlyPhone?.toString() ||
          orderData4CU.accountInfo?.billingAddress?.phone?.toString() ||
          orderData4CU.accountInfo?.mobile?.toString(),
        contact_email: orderData4CU.accountInfo?.recurlyEmail,
        company_name: orderData4CU.accountInfo?.recurlyCompany || '',
        company_address1: orderData4CU.accountInfo?.recurlyAddress,
        company_address2: orderData4CU.accountInfo?.recurlyStreet,
        company_city: orderData4CU.accountInfo?.billingAddress?.city,
        company_state: orderData4CU.accountInfo?.billingAddress?.region || '',
        company_zipcode:
          orderData4CU.accountInfo?.billingAddress?.postalCode || '',
        company_phone:
          orderData4CU.accountInfo?.companyPhone?.toString() ||
          orderData4CU.accountInfo?.billingAddress?.phone?.toString() ||
          orderData4CU.accountInfo?.recurlyPhone?.toString(),
        sales_rep_id: attribution?.salesRepCode
          ? attribution?.salesRepCode
          : null, //need to check
        // on_behalf_of is the selected rep's network ID (same value the 5.0
        // path sends as behalfRepId); it feeds Recurly On_Behalf_Of. The email
        // lives in behalf_email for notifications and display.
        on_behalf_of: attribution?.salesRepCode || null,
        behalf_email: attribution?.behalfEmail || null,
        behalf_first_name: attribution?.behalfFirst || null,
        behalf_last_name: attribution?.behalfLast || null,
        xp_code: attribution?.xpCode || null,
        abn: orderData4CU.accountInfo?.abn
          ? orderData4CU.accountInfo.abn
          : null, //need to check,
        thryv_id: orderData4CU.order?.thryvID
          ? orderData4CU.order.thryvID
          : orderData4CU.accountInfo?.thryvID,
        account_code: orderData4CU.accountInfo?.thryvID
          ? orderData4CU.accountInfo.thryvID
          : orderData4CU.order?.thryvID,
        eaid: orderData4CU.accountInfo?.eaid
          ? orderData4CU.accountInfo?.eaid
          : 'null',
        is_invoice_billed: orderData4CU.accountInfo?.allowInvoice ? 1 : 0,
        is_cohort: null, //need to check,
        demo_lead_id: null,
        no_service: null, //need to check
        onboarding_appt_id: order5OnboardingApptId,
        creative_appt_id: order5CreativeApptId,
        thryv_success_services_appt_id: order5ThryvSuccessServicesApptId,
        update_to_autopay: 1, //check
        on_boarding_fee: 0, //need to check
        purchase_intent_1: null, //need to check,
        purchase_intent_2: null, //need to check,
        purchase_intent_3: null, //need to check,
        sensis_site: null, //need to check,
        lines: [],
        discounts: [],
        reseller: [],
      }

      if (context === 'addons-selection-continue') {
        data.order_status = 'Pending'
        data.lines = contextData
        setOrder4SelectedLines(contextData)
      } else if (context === 'capture-appointment') {
        console.log(
          'order4SelectedLines - upgrade - capture-appointment',
          order4SelectedLines,
        )
        console.log(
          'order4LinesDiscounts - upgrade - capture-appointment',
          order4LinesDiscounts,
        )
        if (contextData.appointment_type === 'creative') {
          data.creative_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'onboarding') {
          data.onboarding_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'thryv-success-services') {
          data.thryv_success_services_appt_id = contextData.apptId
        }
        data.lines = order4SelectedLines
        data.discounts = order4LinesDiscounts
      } else if (context === 'apply-coupon') {
        console.log(
          'order4SelectedLines - upgrade - apply-coupon',
          order4SelectedLines,
        )
        console.log(
          'order4LinesDiscounts - upgrade - apply-coupon',
          order4LinesDiscounts,
        )
        if (contextData.appointment_type === 'creative') {
          data.creative_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'onboarding') {
          data.onboarding_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'thryv-success-services') {
          data.thryv_success_services_appt_id = contextData.apptId
        }
        data.lines = order4SelectedLines
        data.discounts = contextData
        setOrder4LinesDiscounts(contextData)
      } else {
        console.log(
          'order4SelectedLines - upgrade - other context',
          order4SelectedLines,
        )
        console.log(
          'order4LinesDiscounts - upgrade - other context',
          order4LinesDiscounts,
        )
        data.lines = order4SelectedLines
        data.discounts = order4LinesDiscounts
      }

      if (isReseller) {
        const resellerData = getResellerData4Order(data)
        console.log('resellerData', resellerData)
        if (resellerData) {
          data.reseller.push(resellerData)
        }
      }

      if (!order5NUId) {
        console.log('createOrderData - upgrade', data)
        console.log('createOrderData - upgrade (company fields)', {
          company_name: data.company_name,
          company_state: data.company_state,
          company_zipcode: data.company_zipcode,
        })
        const result = await run(
          client('ordercapture/services/createOrder', {data, method: 'POST'}),
        )
        console.log('order created - upgrade', result)
        setOrder5NUId(result.order_id)
        isCreatingOrder.current = false
      } else {
        data.order_id = order5NUId
        console.log('UpdateOrderData - upgrade', data)
        const result = await run(
          client('ordercapture/services/updateOrder', {data, method: 'POST'}),
        )
        console.log('order updated - upgrade', result)
      }
    } catch (error) {
      if (error.status !== 400) {
        console.log(`Error creating/updating order: ${error.message}`)
      }
      if (error.errors) {
        // error.errors.forEach(({message}) => toast.error(message))
        error.errors.forEach(({message}) => console.log(message))
      } else {
        console.log(`Error creating/updating order: ${error.message}`)
      }
      isCreatingOrder.current = false
    }
  }

  const getResellerData4Order = data => {
    if (data.lines && data.lines.length > 0) {
      const resellerData = {
        account_code: orderData4CU.accountInfo.thryvID
          ? orderData4CU.accountInfo.thryvID
          : orderData4CU.order.thryvID,
        account_id: orderData4CU.accountInfo.recurlyAccountId
          ? orderData4CU.accountInfo.recurlyAccountId
          : null,
        no_service: null,
        original_selling_coupon_code: null,
        subscription_id: orderData4CU.order.subscriptionId
          ? orderData4CU.order.subscriptionId
          : null,
        thryv_id: orderData4CU.accountInfo?.thryvId,
        uuid: orderData4CU.order.subscriptionId
          ? orderData4CU.order.subscriptionId
          : null,
      }
      return resellerData
    }
  }

  const onErrorSubscriptionModalclose = () => {
    setShowErrorSubscriptionModal(false)
    navigate('/all-accounts')
  }

  const onErrorAccountclose = () => {
    setShowErrorAccountModal(false)
    navigate('/orders')
  }

  const debouncedOrderCouponCodes = useDebounce(orderCouponCodes, 500)

  const getUpgradeCouponData = useCallback(async () => {
    const coupons = debouncedOrderCouponCodes
    let upgradeCoupons = []
    const transitionCouponCodes = ['25month1', '25month2', '25month3']
    const thryvIdForCoupons =
      order?.CCAccountInfo?.thryvId || accountInfo?.thryvID
    if (!thryvIdForCoupons) {
      setCcUpgradeCoupons([])
      return []
    }
    // OOE-UI-EN: accounts with no redemptions come back as undefined (204 /
    // empty body), and `.some` on that threw out of this callback, aborting
    // coupon setup for the whole upgrade screen. Treat "no data" as "no
    // redemptions".
    const redeemedCouponsResponse = await client(
      `accounts/5/coupon-redemptions?thryvId=${thryvIdForCoupons}`,
    )
    const redeemedCoupons = Array.isArray(redeemedCouponsResponse)
      ? redeemedCouponsResponse
      : []
    const hasTransitionCoupons = redeemedCoupons.some(({code}) =>
      transitionCouponCodes.includes(code),
    )
    // `.centers` was read straight off the optional chain, so a populated
    // `current` without `centers` threw here too. When we can't see the
    // centers we deliberately leave transitionApplies false rather than
    // guessing that the transition coupon is allowed.
    const defaultCenters = defaultCentersAndApps?.current?.centers
    const transitionApplies =
      Array.isArray(defaultCenters) &&
      defaultCenters.filter(
        ({name}) =>
          !name.includes('SEO') &&
          name !== 'Thryv Leads' &&
          !name.includes('Basic'),
      ).length === 0 &&
      !hasTransitionCoupons

    if (coupons.find(x => x.code === 'transition75') && transitionApplies) {
      const {code} = order.selectedCentersAndApps.centers.find(({name}) =>
        CC_PLANS.some(plan => name.toLowerCase().includes(plan)),
      )
      const createUrl = couponCode =>
        `orders/coupon/validate?couponCode=${couponCode}&country=${accountInfo.billingAddress.country}&planCode=${code}`
      const [transitionCoupon1, transitionCoupon2, transitionCoupon3] =
        await Promise.all(
          transitionCouponCodes.map(async code => {
            const url = createUrl(code)
            return await client(url)
          }),
        )
      const transitionCouponIndex = coupons.findIndex(
        addonInfo => addonInfo.code === 'transition75',
      )
      const couponsCopy = [...coupons]

      couponsCopy.splice(transitionCouponIndex, 1)
      couponsCopy.push(transitionCoupon1, transitionCoupon2, transitionCoupon3)

      // captureOrderDiscounts(couponsCopy)
      const COUPON_CODES = couponsCopy
        .filter(coupon => {
          return coupon.isValid
        })
        .map(coupon => {
          return coupon.code
        })
      if (COUPON_CODES.length > 0) {
        upgradeCoupons = COUPON_CODES
      }
      const discountsCoupon = couponsCopy.filter(coupon => {
        return coupon.isValid
      })
      captureOrderDiscounts(discountsCoupon)
    } else {
      const ALL_COUPON_CODES = coupons
        .filter(coupon => {
          return coupon.isValid && coupon.code !== 'transition75'
        })
        .map(coupon => {
          return coupon.code
        })
      if (ALL_COUPON_CODES.length > 0) {
        upgradeCoupons = ALL_COUPON_CODES
      } else {
        upgradeCoupons = []
      }
      const discountsCoupon = coupons.filter(coupon => {
        return coupon.isValid && coupon.code !== 'transition75'
      })
      captureOrderDiscounts(discountsCoupon)
    }

    setCcUpgradeCoupons(upgradeCoupons)
    return upgradeCoupons

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOrderCouponCodes])

  useEffect(() => {
    if (!frontendPreviewPayment || !isUpgrade || !isCCPlan || currentStep < 2)
      return
    if (currentStep >= 2 && currentStep <= 4 && order?.selectedCentersAndApps) {
      getUpgradeCouponData().then(freshCoupons => {
        getPaymentPreview(order, freshCoupons)
      })
    } else {
      getUpgradeCouponData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUpgradeCouponData, currentStep])

  useEffect(() => {
    if (!frontendPreviewPayment) {
      setPreviewRequestFinished(true)
    }
  }, [frontendPreviewPayment])

  useEffect(() => {
    if (order.billingAccountId) fetchAccountInfo()
  }, [fetchAccountInfo, order.billingAccountId])

  useEffect(() => {
    if (!state?.prefetchedEmailValidation?.isInvoiceBilled || !order?.v5upgrade)
      return
    const country =
      accountSubscriptionData?.accountInfo?.businessAddress?.country
    if (!BALANCE_CHECK_COUNTRIES.includes(country)) return
    const thryvId = order.thryvId
    if (!thryvId) return
    client(`invoices/${thryvId}`)
      .then(({openInvoices}) => {
        const hasPastDueInvoices = hasPastDueInvoice(openInvoices)
        if (hasPastDueInvoices) setIsInvoiceBilled(true)
        // else: stays false → catalog shows, no flash
      })
      .catch(() => {
        setIsInvoiceBilled(true) // on error, show invoice page
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // useEffect(() => {
  //   if (
  //     ccCouponInfo &&
  //     ccCouponInfo.length > 0 &&
  //     orderCouponCodes.length === 0
  //   ) {
  //     removeDuplicatesFromArray([...ccCouponInfo], 'code')
  //   }
  // }, [ccCouponInfo, orderCouponCodes])

  // useEffect(() => {
  //   if (
  //     defaultCouponInfo &&
  //     defaultCouponInfo.length > 0 &&
  //     orderCouponCodes &&
  //     Array.isArray(orderCouponCodes)
  //   ) {
  //     // Reset the autoCoupons
  //     resetAutoCoupons()
  //     //Based on the latest default coupon selection, remove incompatible coupons from orderCouponCodes
  //     let filteredOrderCouponCodes
  //     filteredOrderCouponCodes = [...orderCouponCodes]
  //     for (let coupon of defaultCouponInfo) {
  //       let incompatibleCoupons = coupon?.incompatibles?.split(',')
  //       filteredOrderCouponCodes = filteredOrderCouponCodes.filter(
  //         item =>
  //           !incompatibleCoupons.some(
  //             incompatibleCoupon => incompatibleCoupon === item.code,
  //           ),
  //       )
  //     }
  //     setOrderCouponCodes(
  //       removeDuplicatesFromArray(
  //         [...filteredOrderCouponCodes, ...defaultCouponInfo],
  //         'code',
  //       ),
  //     )
  //     getUpgradeCouponData()
  //   }
  // }, [defaultCouponInfo])

  // useEffect(() => {
  //   if (autoCouponInfo && autoCouponInfo.length > 0)
  //     setOrderCouponCodes(
  //       removeDuplicatesFromArray(
  //         [...filteredOrderCouponCodes, ...autoCouponInfo],
  //         'code',
  //       ),
  //     )
  // }, [autoCouponInfo])

  useEffect(() => {
    // Reset the autoCoupons
    resetAutoCoupons()
    if (
      ccCouponInfo &&
      ccCouponInfo.length > 0 &&
      orderCouponCodes.length > 0
    ) {
      // Filter coupons that are incompatible with selected non-default coupon
      let filteredOrderCouponCodes
      let fileteredDefaultCouons
      // Fetch all the incompatible coupons of the selected manual coupons
      let incompatibleCoupons =
        ccCouponInfo.reduce(
          (incompatibles, coupon) =>
            coupon?.incompatibles && coupon?.incompatibles != 'null'
              ? coupon?.incompatibles + ',' + incompatibles
              : incompatibles,
          '',
        ).length > 0
          ? ccCouponInfo
              .reduce(
                (incompatibles, coupon) =>
                  coupon?.incompatibles && coupon?.incompatibles != 'null'
                    ? coupon?.incompatibles + ',' + incompatibles
                    : incompatibles,
                '',
              )
              .split(',')
          : null
      // Remove the incompatible coupon from the orderCouponCodes and defaultCouponInfo
      if (incompatibleCoupons && incompatibleCoupons.length > 0) {
        filteredOrderCouponCodes = orderCouponCodes.filter(
          item =>
            !incompatibleCoupons.some(
              incompatibleCoupon => incompatibleCoupon === item.code,
            ),
        )
        fileteredDefaultCouons = defaultCouponInfo.filter(
          item =>
            !incompatibleCoupons.some(
              incompatibleCoupon => incompatibleCoupon === item.code,
            ),
        )
      } else {
        filteredOrderCouponCodes = [...orderCouponCodes]
        fileteredDefaultCouons = [...defaultCouponInfo]
      }

      if (filteredOrderCouponCodes && filteredOrderCouponCodes.length > 0) {
        setOrderCouponCodes(
          removeDuplicatesFromArray(
            [...ccCouponInfo, ...filteredOrderCouponCodes],
            'code',
          ),
        )
      } else {
        setOrderCouponCodes(
          removeDuplicatesFromArray([...ccCouponInfo], 'code'),
        )
      }
      setDefaultCouponInfo(fileteredDefaultCouons)
    }
  }, [ccCouponInfo])

  return (
    <Container frontendSideNav={frontendSideNav}>
      <MainContainer>
        <WizardContainer>
          {showPastDueInvoiceModal ? (
            SHOW_INVOICE_INFO_FOR_PAST_DUE ? (
              <Modal
                altBtn={<></>}
                openOnLoad
                variant="lg"
                hideX
                onClose={() => setShowPastDueInvoiceModal(false)}
              >
                <InvoiceInformation
                  thryvId={pastDueInvoiceContext?.thryvId}
                  email={pastDueInvoiceContext?.email}
                  selectedCountry={pastDueInvoiceContext?.country}
                  setIsInvoiceBilled={() => setShowPastDueInvoiceModal(false)}
                />
              </Modal>
            ) : (
              <Modal
                altBtn={<></>}
                openOnLoad
                variant="lg"
                footer
                action
                btnAction={() =>
                  navigate(!isReseller ? '/all-accounts' : '/my-accounts')
                }
                onClose={() =>
                  navigate(!isReseller ? '/all-accounts' : '/my-accounts')
                }
                btnActionText="Ok"
                hideX
              >
                <div style={{padding: '20px', textAlign: 'center'}}>
                  <strong>This account has a past due invoice.</strong>
                  <br></br>
                  Please resolve the outstanding balance before processing a new
                  order.
                </div>
              </Modal>
            )
          ) : isInvoiceBilled ? (
            <StepsContainer>
              <NewOrderLayout
                currentStep={currentStep}
                prevStep={prevStep}
                showContinueButton={false}
                title="Invoices"
                showBackButton={true}
                cohortEaid={cohortEaid}
                setCohortEaid={setCohortEaid}
              >
                <InvoiceInformation
                  thryvId={accountInfo?.thryvID}
                  email={accountInfo?.recurlyEmail}
                  setIsInvoiceBilled={setIsInvoiceBilled}
                  selectedCountry={accountInfo?.billingAddress?.country}
                />
              </NewOrderLayout>
            </StepsContainer>
          ) : showLegacyContactsModal ? (
            <Modal
              altBtn={<></>}
              openOnLoad
              variant="lg"
              footer
              action
              btnAction={() => navigate('/')}
              onClose={() => navigate('/')}
              btnActionText="Ok"
              hideX
            >
              <div style={{padding: '20px', textAlign: 'center'}}>
                <strong>
                  A little blast from the past! This account contains Legacy
                  Contacts.
                </strong>
                <br></br>
                For proper handling, please reach out to Client Care at{' '}
                <strong>844-339-7494(Option 1).</strong>
              </div>
            </Modal>
          ) : (
            <>
              {isLoading ? (
                <div className="text-center p-3">
                  <SkeletonLoader width="90%" height="50vh" />
                </div>
              ) : (
                <StepsContainer>
                  {currentStep === 0 && (
                    <CountrySelection nextStep={updateBillingAddress} />
                  )}
                  {currentStep === 1 && !isCCPlan && (
                    <UpgradePackageSelection
                      nextStep={nextStep}
                      prevStep={prevStep}
                      selectedCountry={accountInfo.billingAddress.country}
                      accountInfo={accountInfo}
                      order={order}
                      order5NUId={order5NUId}
                      isCancelOrder5={true}
                      cancelOrder5={updateOrder5Status}
                      isNewTLMCAccessible={isNewTLMCAccessible}
                      hasKeapMaxAccess={hasKeapMaxAccess}
                      isTLLegacyIncluded={isTLLegacyIncluded}
                    />
                  )}
                  {showErrorSubscriptionModal && (
                    <SubscriptionErrorModal
                      onClose={onErrorSubscriptionModalclose}
                    />
                  )}
                  {showErrorAccountModal && (
                    <div>
                      <AccountErrorModal onClose={onErrorAccountclose} />
                    </div>
                  )}
                  {currentStep === 1 &&
                    isCCPlan &&
                    order?.CCAccountInfo &&
                    !showErrorSubscriptionModal &&
                    !showErrorAccountModal && (
                      <Catalog
                        nextStep={nextStep}
                        prevStep={prevStep}
                        updateAddressInfo={updateAddressInfo}
                        selectedCountry={accountInfo?.billingAddress?.country}
                        CCAccountInfo={order.CCAccountInfo}
                        industryTemplate={order.template}
                        prevCentersAndApps={order?.selectedCentersAndApps}
                        prevCode={order?.code}
                        setAccountInfo={setAccountInfo}
                        setAccountSubscriptionData={setAccountSubscriptionData}
                        accountSubscriptionData={accountSubscriptionData}
                        accountInfo={accountInfo}
                        setCurrentStep={setCurrentStep}
                        order={order}
                        setOrder={setOrder}
                        defaultUpgradeCentersAndApps={defaultCentersAndApps}
                        defaultExpiredCentersAndApps={expiredCentersAndApps}
                        all5PlanPrices={catalogPlanPrices}
                        isEaidFound={isEaidFound}
                        setIsEaidFound={setIsEaidFound}
                        cohortEaid={cohortEaid}
                        setCohortEaid={setCohortEaid}
                        setPreviewRequestFinished={setPreviewRequestFinished}
                        setAllowInvoice={setAllowInvoice}
                        allowInvoice={allowInvoice}
                        isCohort={isCohort}
                        setIsCohort={setIsCohort}
                        orderData5CU={orderData5CU}
                        setOrderData5CU={setOrderData5CU}
                        // order5NUId={order5NUId}
                        // setOrder5NUId={setOrder5NUId}
                        captureOrder5NewUpgrade={captureOrder5NewUpgrade}
                        setOrder5LinesDiscounts={setOrder5LinesDiscounts}
                        isWebsiteValid={isWebsiteValid}
                        setIsWebsiteValid={setIsWebsiteValid}
                        isNewTLMCAccessible={isNewTLMCAccessible}
                        hasKeapAccountAbility={hasKeapAccountAbility}
                        setHasKeapAccountAbility={setHasKeapAccountAbility}
                        hasKeapMaxAccess={hasKeapMaxAccess}
                        hasKeapHVACAccess={hasKeapHVACAccess}
                        hasCustomDataFYAccess={hasCustomDataFYAccess}
                        // hasCustomDataServiceAccess={hasCustomDataServiceAccess}
                        keapAccount={keapAccount}
                        setKeapAccount={setKeapAccount}
                        keapID={keapID}
                        setKeapID={setKeapID}
                        setShowErrorAccountModal={setShowErrorAccountModal}
                        setShowErrorSubscriptionModal={
                          setShowErrorSubscriptionModal
                        }
                        navFrom={navFrom}
                        isBillingNoticeDisplayed={isBillingNoticeDisplayed}
                        setIsBillingNoticeDisplayed={
                          setIsBillingNoticeDisplayed
                        }
                        keapTrailAppId={keapTrailAppId}
                        setKeapTrailAppId={setKeapTrailAppId}
                        setKeapTextVoiceQty={setKeapTextVoiceQty}
                        setKeapTextVoiceQtyAnnual={setKeapTextVoiceQtyAnnual}
                        setKeapAddContactsQty={setKeapAddContactsQty}
                        setKeapAddContactsQtyAnnual={
                          setKeapAddContactsQtyAnnual
                        }
                        setKeapAddContactsQtyGraphlyAnnual={
                          setKeapAddContactsQtyGraphlyAnnual
                        }
                        setPlusThisPremiumQtyGraphlyAnnual={
                          setPlusThisPremiumQtyGraphlyAnnual
                        }
                        setKeapAddContactsQtyGraphlyM2m={
                          setKeapAddContactsQtyGraphlyM2m
                        }
                        setPlusThisPremiumQtyGraphlyM2m={
                          setPlusThisPremiumQtyGraphlyM2m
                        }
                        prefetchedAccountSubscriptions={
                          state?.prefetchedAccountSubscriptions
                        }
                      />
                    )}
                  {currentStep === 2 && !isCCPlan && (
                    <UpgradeAddonsSelection
                      nextStep={nextStep}
                      prevStep={prevStep}
                      selectedPlan={order.selectedPlan}
                      billingPreference={order.billingPreference}
                      selectedCountry={
                        COUNTRIES[accountInfo.billingAddress.country]?.value
                      }
                      selectedCountryAbbreviation={
                        accountInfo.billingAddress.country
                      }
                      currentGmailItems={getGmailItems()}
                      industryCategoryType={order?.selectedPlan?.industryType}
                      selectedIndustry={accountInfo.industry}
                      accountInfo={accountInfo}
                      accountSubscriptionData={accountSubscriptionData}
                      isWebsiteIncluded={isWebsiteIncluded}
                      all5PlanPrices={all5PlanPrices}
                      isTSS={isTSS}
                      isEaidFound={isEaidFound}
                      cohortEaid={cohortEaid}
                      setIsEaidFound={setIsEaidFound}
                      setCohortEaid={setCohortEaid}
                      setAllowInvoice={setAllowInvoice}
                      setIsCohort={setIsCohort}
                      orderData4CU={orderData4CU}
                      order5NUId={order5NUId}
                      isCancelOrder5={true}
                      cancelOrder5={updateOrder5Status}
                      captureOrder4NewUpgrade={captureOrder4NewUpgrade}
                      isNewTLMCAccessible={isNewTLMCAccessible}
                      hasKeapMaxAccess={hasKeapMaxAccess}
                      hasKeapHVACAccess={hasKeapHVACAccess}
                      hasCustomDataFYAccess={hasCustomDataFYAccess}
                      // hasCustomDataServiceAccess={hasCustomDataServiceAccess}
                      isBillingNoticeDisplayed={isBillingNoticeDisplayed}
                      setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
                    />
                  )}
                  {((currentStep === 3 && !isCCPlan) ||
                    (currentStep === 2 && isCCPlan)) && (
                    <OnboardingProgram
                      nextStep={nextStep}
                      prevStep={prevStep}
                      all5PlanPrices={all5PlanPrices}
                      selectedPlan={order.selectedPlan}
                      billingPreference={order.billingPreference}
                      accountInformation={{
                        mobile:
                          accountInfo?.billingAddress?.mobile ||
                          accountInfo?.recurlyPhone,
                        email: accountInfo?.recurlyEmail,
                        lastName: accountInfo?.recurlyLast,
                        firstName: accountInfo?.recurlyFirst,
                        country: accountInfo?.billingAddress?.country,
                        state:
                          accountInfo?.billingAddress?.region ||
                          accountInfo?.billingAddress?.state,
                        city: accountInfo?.billingAddress?.city,
                        address: accountInfo?.billingAddress?.street1,
                        company: accountInfo?.recurlyCompany,
                        selectedPackage: selectedPlanCode,
                        addOns: accountInfo?.addOns,
                        hasPaidOnboardingFee:
                          accountInfo?.hasPaidOnboardingFee ||
                          accountSubscriptionData?.accountInfo
                            ?.hasPaidOnboardingFee ||
                          false,
                        hasPaidDesignFee:
                          accountInfo?.hasPaidDesignFee ||
                          accountSubscriptionData?.accountInfo
                            ?.hasPaidDesignFee ||
                          false,
                      }}
                      industryCategoryType={
                        isCCPlan
                          ? order.template
                          : order.selectedPlan.industryType
                      }
                      selectedAddons={order.selectedAddons}
                      allSelectedPurchaseIntent={allSelectedPurchaseIntent}
                      setAllSelectedPurchaseIntent={
                        setAllSelectedPurchaseIntent
                      }
                      currentPlan={accountInfo.planCode}
                      currentPlanName={accountInfo.planName}
                      setKickoffApptId={setKickoffApptId}
                      kickoffApptId={kickoffApptId}
                      creativeApptId={creativeApptId}
                      setCreativeApptId={setCreativeApptId}
                      thryvSuccessServicesApptId={thryvSuccessServicesApptId}
                      setThryvSuccessServicesApptId={
                        setThryvSuccessServicesApptId
                      }
                      setCartProducts={setCartProducts}
                      cartProducts={cartProducts}
                      isWebsiteIncluded={isWebsiteIncluded}
                      isSocialBoostIncluded={isSocialBoostIncluded}
                      onboardingCallDate={onboardingCallDate}
                      setOnboardingCallDate={setOnboardingCallDate}
                      setCreativeCallDate={setCreativeCallDate}
                      creativeCallDate={creativeCallDate}
                      setThryvSuccessServicesCallDate={
                        setThryvSuccessServicesCallDate
                      }
                      thryvSuccessServicesCallDate={
                        thryvSuccessServicesCallDate
                      }
                      selectedCountry={
                        COUNTRIES[accountInfo.billingAddress.country]
                      }
                      selectedCentersAndApps={order.selectedCentersAndApps}
                      defaultCentersAndApps={defaultCentersAndApps.current}
                      expiredCentersAndApps={expiredCentersAndApps.current}
                      upgradeAccountInfo={accountInfo}
                      previewRequestFinished={previewRequestFinished}
                      eligibleTransitionCenters={eligibleTransitionCenters}
                      isCohort={isCohort}
                      order5NUId={order5NUId}
                      isCancelOrder5={true}
                      cancelOrder5={updateOrder5Status}
                      captureOrderNewUpgrade={
                        isCCPlan
                          ? captureOrder5NewUpgrade
                          : captureOrder4NewUpgrade
                      }
                      order5OnboardingApptId={order5OnboardingApptId}
                      order5CreativeApptId={order5CreativeApptId}
                      order5ThryvSuccessServicesApptId={
                        order5ThryvSuccessServicesApptId
                      }
                      setOrder5CreativeApptId={setOrder5CreativeApptId}
                      setOrder5ThryvSuccessServicesApptId={
                        setOrder5ThryvSuccessServicesApptId
                      }
                      setOrder5OnboardingApptId={setOrder5OnboardingApptId}
                      isWebsiteValid={isWebsiteValid}
                      isNewTLMCAccessible={isNewTLMCAccessible}
                      keapAccount={keapAccount}
                      isBillingNoticeDisplayed={isBillingNoticeDisplayed}
                      setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
                      order={order}
                    />
                  )}
                  {((currentStep === 4 && !isCCPlan) ||
                    (currentStep === 3 && isCCPlan)) && (
                    <PaymentDetails
                      sendPaymentLink={sendPaymentLink}
                      resendPaymentLink={resendPaymentLink}
                      checkClientEmailStatus={checkClientEmailStatus}
                      showSmsOption={showSmsOption}
                      setShwoSmsOption={setShwoSmsOption}
                      prevStep={prevStep}
                      nextStep={nextStep}
                      selectedAddons={order.selectedAddons}
                      accountInformation={{
                        mobile:
                          accountInfo.mobile ||
                          accountInfo.billingAddress.mobile ||
                          accountInfo.recurlyPhone,
                        package: selectedPlanCode,
                      }}
                      planCode={selectedPlanCode}
                      countryIsoValue={accountInfo.billingAddress.country}
                      setKickoffApptId={setKickoffApptId}
                      kickoffApptId={kickoffApptId}
                      creativeApptId={creativeApptId}
                      setCreativeApptId={setCreativeApptId}
                      thryvSuccessServicesApptId={thryvSuccessServicesApptId}
                      setThryvSuccessServicesApptId={
                        setThryvSuccessServicesApptId
                      }
                      accountInfo={accountInfo}
                      accountSubscriptionData={accountSubscriptionData}
                      selectedPlan={order.selectedPlan}
                      setCartProducts={setCartProducts}
                      cartProducts={cartProducts}
                      processing={processing}
                      setProcessing={setProcessing}
                      selectedCountry={accountInfo.billingAddress.country}
                      setCreativeCallDate={setCreativeCallDate}
                      setOnboardingCallDate={setOnboardingCallDate}
                      clientEmail={accountInfo.recurlyEmail}
                      selectedCentersAndApps={order?.selectedCentersAndApps}
                      sendCCPaymentLink={sendCCPaymentLink}
                      onboardingCallDate={onboardingCallDate}
                      creativeCallDate={creativeCallDate}
                      thryvSuccessServicesCallDate={
                        thryvSuccessServicesCallDate
                      }
                      isCCPlan={isCCPlan}
                      defaultCentersAndApps={defaultCentersAndApps.current}
                      expiredCentersAndApps={expiredCentersAndApps.current}
                      isSEOIncluded={isSEOIncluded}
                      isInvoiceBilled={isInvoiceBilled}
                      allowInvoice={allowInvoice}
                      isCohort={isCohort}
                      setSelectedPaymentMethod={setSelectedPaymentMethod}
                      previewRequestFinished={previewRequestFinished}
                      selectedPaymentMethod={selectedPaymentMethod}
                      cohortEaid={cohortEaid}
                      setCohortEaid={setCohortEaid}
                      orderData5CU={orderData5CU}
                      orderData4CU={orderData4CU}
                      order5NUId={order5NUId}
                      isCancelOrder5={true}
                      cancelOrder5={updateOrder5Status}
                      updateApptOnExpire={updateApptOnExpire}
                      captureOrder4NewUpgrade={captureOrder4NewUpgrade}
                      isNewTLMCAccessible={isNewTLMCAccessible}
                      isWebsiteValid={isWebsiteValid}
                      keapAccount={keapAccount}
                      isBillingNoticeDisplayed={isBillingNoticeDisplayed}
                      setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
                      all5PlanPrices={all5PlanPrices}
                      order={order}
                      keapTrailAppId={keapTrailAppId}
                      setKeapTrailAppId={setKeapTrailAppId}
                      keapTextVoiceQty={keapTextVoiceQty}
                      keapAddContactsQty={keapAddContactsQty}
                      getLegacyPaymentPreview={getLegacyPaymentPreview}
                      keapAddContactsQtyAnnual={keapAddContactsQtyAnnual}
                      keapTextVoiceQtyAnnual={keapTextVoiceQtyAnnual}
                      keapAddContactsQtyGraphlyAnnual={
                        keapAddContactsQtyGraphlyAnnual
                      }
                      plusThisPremiumQtyGraphlyAnnual={
                        plusThisPremiumQtyGraphlyAnnual
                      }
                    />
                  )}
                  {currentStep === 5 && isTSS && !isCCPlan && (
                    <TSSConfirmation />
                  )}
                </StepsContainer>
              )}
            </>
          )}
        </WizardContainer>
      </MainContainer>
    </Container>
  )
}

export {UpgradeOrder}
